import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import TcaCieemgEmpregador from "../TcaEmpregador/index.js";
import "./style.css";
import InputMask from "../../components/InputMask/index.js";
import { NumericFormat } from "react-number-format";
import { useForm } from "react-hook-form";
import { TbDoorExit } from "react-icons/tb";
import { jwtDecode } from "jwt-decode";
import { API_ENDPOINTS, API_BASE_URL } from "../../../js/api.js";
import { toDateInputValue } from "../../../js/dateHelpers.js";
import FormReview from "../../components/Compos Validation/index.js";
import MissingFieldsAlert from "../../components/Fill-inAlert/index.js";
import ToastContainer from "../../components/Toast/ToastContainer.js";
import useToast from "../../hooks/useToast.js";
import useConfirm from "../../hooks/useConfirm.js";
import DocumentUploadForm from "../../components/DocumentUploadForm/index.js";
import SuccessSubmitModal from "../../components/SuccessSubmitModal/index.js";
import {
  buildAnexosObrigatoriosIds,
  buildAnexosObrigatoriosBodyPatch,
  parseAnexosObrigatoriosFromError,
  hydrateAnexosFilesFromPayload,
} from "../../js/anexosObrigatorios.js";
import {
  Check,
  CheckCircle2,
  AlertCircle,
  Circle,
  Upload,
  Paperclip,
  Home,
  GraduationCap,
  IdCard,
  FileText,
} from "lucide-react";

const LINK_DOCUSIGN_TCA_CIEEMG_FALLBACK =
  "https://na2.docusign.net/Member/PowerFormSigning.aspx?PowerFormId=332d1eb3-e52c-497a-bcf9-da18ae0d0234&env=na2&acct=272fb0e3-8eca-4797-ac82-b4ba543c625f&v=2";

const ANEXO_ID_SUFFIXES_TO_STRIP = ["_path", "_nome"];

function normalizeMissingItemId(id) {
  if (typeof id !== "string") return id;
  for (const suffix of ANEXO_ID_SUFFIXES_TO_STRIP) {
    if (id.endsWith(suffix)) return id.slice(0, -suffix.length);
  }
  return id;
}

function dedupeMissingItems(items) {
  if (!Array.isArray(items)) return [];
  const seen = new Set();
  const result = [];
  items.forEach((item) => {
    if (!item || item.id == null) return;
    if (seen.has(item.id)) return;
    seen.add(item.id);
    result.push(item);
  });
  return result;
}

const STATUS_CORES = {
  PENDENTE: "#3b82f6",
  APROVADO: "#1e3a8a",
  RECUSADO: "#dc2626",
};

export const formatDateBR = (dateString) => {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return dateString;
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

export const formatarCnpjCpf = (valor) => {
  if (!valor) return "";

  const nums = valor.replace(/\D/g, "").slice(0, 14);
  if (nums.length === 0) return "";
  if (nums.length <= 11) {
    return nums
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  } else {
    return nums
      .replace(/^(\d{2})(\d)/, "$1.$2")
      .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/\.(\d{3})(\d)/, ".$1/$2")
      .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
  }
};

export const isTelefonePlaceholder = (valor) => {
  if (!valor) return false;
  const nums = String(valor).replace(/\D/g, "");
  if (nums.length < 8) return false;
  return /^(\d)\1+$/.test(nums);
};

export const sanitizeTelefone = (valor) => {
  if (!valor) return "";
  if (isTelefonePlaceholder(valor)) return "";
  return String(valor);
};

const logEndpointError = (label, err, path) => {
  const status = err?.response?.status;
  if (status === 404 || status === 405) {
    return;
  }
  console.warn(
    `[TCA] Falha ao carregar ${label} (${path}):`,
    status || "sem status",
    err?.response?.data || err?.message,
  );
};

export const formatHorarioTeoricoLabel = (h) => {
  const inicio = h?.horario_inicio;
  const fim = h?.horario_fim;
  if (!inicio || !fim) return "";
  return `${inicio} às ${fim}`;
};

const __DEV__ = process.env.NODE_ENV !== "production";
const debugLog = (...args) => {
  if (__DEV__) console.log(...args);
};

const getAutorizacao = async (empresaId) => {
  try {
    const token = localStorage.getItem("token");
    const response = await axios.get(
      `${API_BASE_URL}/tca/empresa/${empresaId}/autorizacao`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  } catch (error) {
    console.error("Erro autorização:", error);
    return null;
  }
};

export const parseSalario = (val) => {
  if (val === null || val === undefined) return 0;
  if (typeof val === "number") return val;
  const cleaned = String(val)
    .replace("R$", "")
    .replace(/\s/g, "")
    .replace(/\./g, "")
    .replace(",", ".");
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
};

export const calcularSalarioMinimoPorCargaHoraria = (
  salarioMinimoVigente,
  horasDiarias,
) => {
  if (horasDiarias !== 4 && horasDiarias !== 6) return null;
  const horasSemanais = horasDiarias * 5;
  const valorHora = Math.round((salarioMinimoVigente / 220) * 100) / 100;
  const valorSemanal = valorHora * horasSemanais;
  return Math.round(((valorSemanal * 4.4285 * 7) / 6) * 100) / 100;
};

export const buildMensagemSalarioInferiorMinimo = (
  salarioInformado,
  salarioMinimoCalculado,
  horasDiarias,
) => {
  const fmt = (v) =>
    Number(v || 0).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  return `O salário informado (R$ ${fmt(salarioInformado)}) é inferior ao valor mínimo calculado pelo sistema (R$ ${fmt(salarioMinimoCalculado)}) para a carga horária de ${horasDiarias} horas diárias.`;
};

/**
 * Verifica se o salário informado é inferior ao mínimo calculado.
 * Retorna `null` quando está ok, ou um objeto { salarioMinimoCalculado, horasDiarias }
 * pronto para virar mensagem de erro.
 */
export const validarSalarioMinimo = (
  salarioInformado,
  salarioMinimoVigente,
  horasDiarias,
) => {
  const salarioMinimoCalculado = calcularSalarioMinimoPorCargaHoraria(
    salarioMinimoVigente,
    horasDiarias,
  );
  if (salarioMinimoCalculado === null) return null;
  if (salarioInformado < salarioMinimoCalculado) {
    return { salarioMinimoCalculado, horasDiarias };
  }
  return null;
};

export const calcularIdade = (dataNascimento, hoje = new Date()) => {
  if (!dataNascimento) return null;
  const nasc = new Date(dataNascimento);
  if (isNaN(nasc.getTime())) return null;

  let idade = hoje.getFullYear() - nasc.getFullYear();
  const m = hoje.getMonth() - nasc.getMonth();
  if (m < 0 || (m === 0 && hoje.getDate() < nasc.getDate())) {
    idade -= 1;
  }
  return idade;
};

export const isMenorDeIdade = (idade) => {
  if (idade === null || idade === undefined) return false;
  return idade < 18;
};

export const getLimiteHorasAprendizagem = (cursos, cursoValor) => {
  if (!Array.isArray(cursos) || !cursoValor) return null;

  const cursoSelecionado = cursos.find((curso) => {
    if (!curso) return false;
    if (curso.valorObj) {
      const fullDesc = `${curso.valorObj.curso} — ${curso.valorObj.carga_horaria} (${curso.valorObj.modalidade})`;
      const cursoModalidade = `${curso.valorObj.curso} (${curso.valorObj.modalidade})`;
      return (
        fullDesc === cursoValor ||
        cursoModalidade === cursoValor ||
        curso.valorObj.curso === cursoValor ||
        String(curso.id) === String(cursoValor)
      );
    }
    return (
      String(curso.id) === String(cursoValor) || curso.curso === cursoValor
    );
  });

  if (!cursoSelecionado) return null;

  const cargaHoraria = Number(
    cursoSelecionado.valorObj?.carga_horaria ??
      cursoSelecionado.carga_horaria ??
      cursoSelecionado.cargaHoraria,
  );

  if (cargaHoraria === 1936) return 4;
  if (cargaHoraria === 2230) return 6;
  return null;
};

const TCA = () => {
  const navigate = useNavigate();

  const token = localStorage.getItem("token");
  let tipoPrograma = 1;
  try {
    const decoded = jwtDecode(token);
    tipoPrograma = Number(decoded.tipo_programa);
  } catch (e) {
    window.location.href = "/";
    return null;
  }

  if (tipoPrograma === 2) {
    return <TcaCieemgEmpregador />;
  }

  return <TcaComum />;
};

// ============================================
// TCA Comum (formato antigo, /tca)
// ============================================
const TcaComum = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));
  const isStudent = Number(user?.acesso_autorizacao) === 1;
  const isEmpresaReadOnly = [0, 1].includes(Number(user?.acesso_autorizacao));
  const submittingRef = useRef(false);
  const {
    toasts,
    error: toastError,
    success: toastSuccess,
    warning: toastWarning,
    removeToast,
  } = useToast();
  const {
    confirm,
    prompt: promptUser,
    confirmDialog: confirmDialogComum,
  } = useConfirm();

  const ANEXOS_SCHEMA_TCA_COMUM = [
    {
      id: "anexo_comprovante_endereco",
      label: "Comprovante de Endereço",
      icon: "Home",
      // Responsabilidade EXCLUSIVA do aprendiz — empresa vê bloqueado.
      required: true,
    },
    {
      id: "anexo_historico_conclusao",
      label: "Declaração escolar e/ou histórico de conclusão do Ensino Médio",
      hint: "Até 30 dias de emissão",
      icon: "GraduationCap",
      // Responsabilidade EXCLUSIVA do aprendiz — empresa vê bloqueado.
      required: true,
    },
    {
      id: "anexo_documento_identificacao",
      label: "Documento de Identificação (RG ou CNH)",
      icon: "IdCard",
      // Responsabilidade EXCLUSIVA do aprendiz — empresa vê bloqueado.
      required: true,
    },
  ];

  const getAnexosObrigatoriosFaltandoTcaComum = () => {
    if (!isStudent) return [];
    return ANEXOS_SCHEMA_TCA_COMUM.filter((doc) => {
      if (!doc.required) return false;
      const entry = anexosFiles[doc.id];
      const isFilePicked = entry instanceof File || entry?.file;
      const hasFilename = !!(entry && entry.filename);
      return !(isFilePicked && hasFilename);
    });
  };

  const getStatusCor = (status) => {
    const s = String(status || "").toUpperCase();

    if (s === "INÍCIO" || s === "INICIO") return "#005ac2";
    if (s.includes("APROVADO") || s.includes("APROVAR")) return "#16a34a";
    if (
      s.includes("REJEITADO") ||
      s.includes("REPROVADO") ||
      s.includes("RECUSADO") ||
      s.includes("REJEITAR") ||
      s.includes("REPROVAR")
    )
      return "#dc2626";
    if (
      s.includes("PENDENTE") ||
      s.includes("AGUARDANDO") ||
      s.includes("EM_VALIDACAO") ||
      s.includes("CONTRATO_CONFECCIONADO") ||
      s.includes("DISPONIBILIZADO")
    )
      return "#eab308";

    return "#3b82f6";
  };

  const [autorizado, setAutorizado] = useState(false);
  const [successModalData, setSuccessModalData] = useState({
    isOpen: false,
    tceId: null,
    role: "empresa",
  });
  const [missingFieldsAlert, setMissingFieldsAlert] = useState([]);
  const [emailErrors, setEmailErrors] = useState({});
  const handleMissingFieldClick = (fieldId) => {
    setMissingFieldsAlert([]);
    setTimeout(() => {
      if (typeof fieldId === "string" && fieldId.startsWith("doc_")) {
        const el = document.getElementById(`file-input-${fieldId}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          if (typeof el.focus === "function") {
            try {
              el.focus({ preventScroll: true });
            } catch (_) {
              el.focus();
            }
          }
        }
        return;
      }

      const el =
        document.getElementById(fieldId) ||
        document.querySelector(`[name="${fieldId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        if (typeof el.focus === "function") {
          try {
            el.focus({ preventScroll: true });
          } catch (_) {
            el.focus();
          }
        }
      }
    }, 150);
  };

  const [formData, setFormData] = useState({
    empresa_id: "",
    razao_social: "",
    cnpj_ou_cpf: "",
    email_empresa: "",
    nome_empresa: "",
    cargo_empresa: "",
    telefone_empresa_primario: "",
    telefone_empresa: "",
    data_inicio_contrato: "",
    data_fim_contrato: "",
    salario: "",
    tutor_aprendiz_empresa: "",
    cargo_tutor: "",
    nome_tutor: "",
    email_tutor: "",
    email_empresa_contratante: "",
    endereco_atividade_teorica: "",
    regiao: "",
    endereco_tutor: "",
    carga_horario_curso: "",
    curso: "",
    nome_aprendiz: "",
    data_nascimento: "",
    telefone_contato: "",
    email_aprendiz: "",
    atividades_aprendiz: "",
    local_realizacao_atividades: "",
    nome_aprendiz_menor_idade: "",
    telefone_aprendiz_menor_idade: "",
    email_aprendiz_menor_idade: "",
    cpf_aprendiz_menor_idade: "",
    observacoes1: "",
    observacoes2: "",
    horario_inicio_horas: "",
    horario_inicio_minutos: "",
    horario_fim_horas: "",
    horario_fim_minutos: "",
    local_execucao_diferente: false,
    emp_local_razao_social: "",
    emp_local_cnpj: "",
    emp_local_telefone: "",
    emp_local_email: "",
    emp_local_endereco: "",
    emp_local_bairro: "",
    emp_local_cidade: "",
    emp_local_uf: "",
    emp_local_cep: "",
    emp_local_rep_nome: "",
    emp_local_rep_cargo: "",
    emp_local_rep_telefone: "",
    emp_local_rep_email: "",
    resp_admin_nome: "",
    resp_admin_cargo: "",
    resp_admin_telefone: "",
    resp_admin_email: "",
    nome_filial: "",
    cpfCnpj: "",
    telefones: "",
    telefones2: "",
    bairro: "",
    municipio: "",
    uf: "",
    emailContratos: "",
    emailFinanceiro: "",
    representadaPor: "",
    cargoRepresentante: "",
    emailRepresentante: "",
    responsavelSupervisor: "",
    cargoSupervisor: "",
    emailSupervisor: "",
  });

  const [permissao, setPermissao] = useState({});
  const [showObs1, setShowObs1] = useState(false);
  const [showObs2, setShowObs2] = useState(false);
  const matrizEnderecoTeoricoRef = useRef("");
  // Refs para detectar se o usuário editou manualmente os endereços
  // da aprendizagem prática e teórica. Quando true, o auto-preenchimento
  // baseado em Matriz/Filial/local de execução é suspenso para preservar
  // o que o usuário digitou.
  const enderecoTutorEditadoManualmenteRef = useRef(false);
  const enderecoTeoricaEditadoManualmenteRef = useRef(false);
  const [filiais, setFiliais] = useState([]);
  const [mostrarFiliais, setMostrarFiliais] = useState(false);
  const [filialLoading, setFilialLoading] = useState(false);
  const [dadosFilialSelecionada, setDadosFilialSelecionada] = useState(null);
  const [localAtividade, setLocalAtividade] = useState("");
  const [anexosFiles, setAnexosFiles] = useState({});
  const [anexosProgress, setAnexosProgress] = useState({});
  const [anexosStatus, setAnexosStatus] = useState({});
  const [anexosObrigatoriosDestacar, setAnexosObrigatoriosDestacar] = useState(
    [],
  );
  const [idadeCalculada, setIdadeCalculada] = useState(null);
  const carregouEmpresaRef = useRef(false);
  const [tcaHistorico, setTcaHistorico] = useState([]);
  const [modoEdicao, setModoEdicao] = useState(false);
  const horarioValidoAlertadoRef = useRef(false);
  const [showReview, setShowReview] = useState(false);
  const [salarioMinimoVigente, setSalarioMinimoVigente] = useState(1621.0);
  const [estudanteNaoEncontrado, setEstudanteNaoEncontrado] = useState(false);
  const [cienteCadastro, setCienteCadastro] = useState(false);
  const [unidadesCieemg, setUnidadesCieemg] = useState([]);
  const [horariosTeoricos, setHorariosTeoricos] = useState([]);
  const [horario, setHorario] = useState({
    tipo: "fixo",
    diasSelecionados: ["Segunda", "Terça", "Quarta", "Quinta", "Sexta"],
    fixo: {
      entrada: "",
      saida: "",
      intervaloInicio: "",
      intervaloFim: "",
    },
    alternado: {
      segunda: {
        entrada: "",
        saida: "",
        intervaloInicio: "",
        intervaloFim: "",
      },
      terca: { entrada: "", saida: "", intervaloInicio: "", intervaloFim: "" },
      quarta: { entrada: "", saida: "", intervaloInicio: "", intervaloFim: "" },
      quinta: { entrada: "", saida: "", intervaloInicio: "", intervaloFim: "" },
      sexta: { entrada: "", saida: "", intervaloInicio: "", intervaloFim: "" },
    },
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("id");
    if (id) {
      setModoEdicao(true);
    }
  }, []);

  useEffect(() => {
    let entrada = "";
    let saida = "";
    if (horario.tipo === "fixo") {
      entrada = horario.fixo.entrada;
      saida = horario.fixo.saida;
    } else {
      const dias = [
        "segunda",
        "terca",
        "quarta",
        "quinta",
        "sexta",
        "sabado",
        "domingo",
      ];
      for (const d of dias) {
        if (
          horario.alternado[d] &&
          horario.alternado[d].entrada &&
          horario.alternado[d].saida
        ) {
          entrada = horario.alternado[d].entrada;
          saida = horario.alternado[d].saida;
          break;
        }
      }
    }

    setFormData((prev) => {
      let next = { ...prev };
      if (entrada) {
        const [h, m] = entrada.split(":");
        next.horario_inicio_horas = h || "";
        next.horario_inicio_minutos = m || "";
      } else {
        next.horario_inicio_horas = "";
        next.horario_inicio_minutos = "";
      }

      if (saida) {
        const [h, m] = saida.split(":");
        next.horario_fim_horas = h || "";
        next.horario_fim_minutos = m || "";
      } else {
        next.horario_fim_horas = "";
        next.horario_fim_minutos = "";
      }
      return next;
    });
  }, [horario]);

  function horasDecimais(
    entrada,
    saida,
    intervaloInicio = "",
    intervaloFim = "",
  ) {
    if (!entrada || !saida) return 0;
    const [eh, em] = entrada.split(":").map(Number);
    const [sh, sm] = saida.split(":").map(Number);
    let totalMin = sh * 60 + sm - (eh * 60 + em);
    if (totalMin < 0) totalMin += 24 * 60;
    if (intervaloInicio && intervaloFim) {
      const [ih, im] = intervaloInicio.split(":").map(Number);
      const [fh, fm] = intervaloFim.split(":").map(Number);
      let intervaloMin = fh * 60 + fm - (ih * 60 + im);
      if (intervaloMin < 0) intervaloMin += 24 * 60;
      totalMin -= intervaloMin;
    }
    return totalMin / 60;
  }

  function validarHorarios(horarioObj) {
    const errors = [];
    let totalSemanal = 0;
    if (horarioObj.tipo === "fixo") {
      const f = horarioObj.fixo;
      if (f.entrada && f.saida) {
        const horas = horasDecimais(
          f.entrada,
          f.saida,
          f.intervaloInicio,
          f.intervaloFim,
        );
        if (horas > 6)
          errors.push("Carga horária diária não pode ultrapassar 6h");
        if (horas >= 6 && (!f.intervaloInicio || !f.intervaloFim)) {
          errors.push("Intervalo obrigatório para jornada de 6h");
        }
        totalSemanal = horas * horarioObj.diasSelecionados.length;
      }
    } else if (horarioObj.tipo === "alternado") {
      const diasMap = {
        segunda: "Segunda",
        terca: "Terça",
        quarta: "Quarta",
        quinta: "Quinta",
        sexta: "Sexta",
      };
      Object.keys(horarioObj.alternado).forEach((key) => {
        const h = horarioObj.alternado[key];
        if (h.entrada && h.saida) {
          const horas = horasDecimais(
            h.entrada,
            h.saida,
            h.intervaloInicio,
            h.intervaloFim,
          );
          if (horas > 6)
            errors.push(`${diasMap[key]}: carga horária superior a 6h`);
          totalSemanal += horas;
        }
      });
    }
    if (totalSemanal > 30)
      errors.push(
        `Carga horária semanal (${totalSemanal.toFixed(1)}h) superior ao limite de 30h`,
      );
    return { errors, totalSemanal };
  }

  useEffect(() => {
    const filialId = String(localAtividade || "").trim();
    if (!filialId || filialId === "MATRIZ" || isNaN(Number(filialId))) {
      const camposFilial = [
        "nome_filial",
        "cpfCnpj",
        "telefones",
        "telefones2",
        "bairro",
        "municipio",
        "uf",
        "emailContratos",
        "emailFinanceiro",
        "representadaPor",
        "cargoRepresentante",
        "emailRepresentante",
        "responsavelSupervisor",
        "cargoSupervisor",
        "emailSupervisor",
      ];
      setFormData((prev) => {
        const next = { ...prev };
        camposFilial.forEach((c) => {
          next[c] = "";
        });
        // Volta o endereço teórico para o da matriz quando o usuário
        // troca de filial para matriz, a menos que ele tenha editado
        // manualmente o campo.
        if (
          matrizEnderecoTeoricoRef.current &&
          !enderecoTeoricaEditadoManualmenteRef.current
        ) {
          next.endereco_atividade_teorica = matrizEnderecoTeoricoRef.current;
        }
        return next;
      });
      return;
    }

    let cancelled = false;

    const carregarFilial = async () => {
      setFilialLoading(true);
      try {
        const token = localStorage.getItem("token");
        const url = API_ENDPOINTS.filial_id(filialId);
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (cancelled) return;

        const dados = response.data || {};

        const mapeamento = [
          ["nome_filial", dados.nome_filial],
          ["cpfCnpj", dados.cnpj],
          ["telefones", dados.telefone_1],
          ["telefones2", dados.telefone_2],
          ["bairro", dados.bairro],
          ["municipio", dados.cidade],
          ["uf", dados.uf],
          ["emailContratos", dados.email_contratos],
          ["emailFinanceiro", dados.email_financeiro],
          ["representadaPor", dados.representante_nome],
          ["cargoRepresentante", dados.representante_cargo],
          ["emailRepresentante", dados.representante_email],
          ["responsavelSupervisor", dados.supervisor_nome],
          ["cargoSupervisor", dados.supervisor_cargo],
          ["emailSupervisor", dados.supervisor_email],
          [
            "endereco_atividade_teorica",
            dados.unidadeTeorica ? dados.unidadeTeorica.endereco : "",
          ],
        ];

        setFormData((prev) => {
          const next = { ...prev };
          mapeamento.forEach(([campo, valor]) => {
            if (valor !== undefined && valor !== null) {
              next[campo] = valor;
            }
          });
          return next;
        });

        toastSuccess("Dados da filial carregados.");
      } catch (err) {
        if (cancelled) return;
        const status = err.response?.status;
        const backendMsg =
          (Array.isArray(err.response?.data?.errors) &&
            err.response.data.errors.join(", ")) ||
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          null;
        console.error(
          `[TCA][Filial] erro ao carregar filial id=${filialId} status=${status}:`,
          err.response?.data,
        );
        if (status === 404) {
          toastError(
            backendMsg
              ? `Filial não encontrada: ${backendMsg}`
              : "Filial não encontrada",
          );
        } else if (status >= 500) {
          toastError(
            backendMsg
              ? `Erro no servidor ao buscar filial: ${backendMsg}`
              : "Erro no servidor ao buscar filial",
          );
        } else if (status === 401 || status === 403) {
          toastError("Sessão expirada ou sem permissão. Faça login novamente.");
        } else {
          toastError(
            backendMsg
              ? `Erro ao carregar filial: ${backendMsg}`
              : "Erro ao carregar dados da filial",
          );
        }
      } finally {
        if (!cancelled) setFilialLoading(false);
      }
    };

    carregarFilial();
    return () => {
      cancelled = true;
    };
  }, [localAtividade, toastError, toastSuccess]);

  const buildReviewData = () => {
    const user = JSON.parse(localStorage.getItem("user"));
    const nivel = Number(user?.acesso_autorizacao ?? 0);
    const isEmpresa =
      user?.tipo === "empresa" ||
      user?.tipo === "usuario_externo" ||
      (user?.tipo === "usuario_ciee" && nivel === 0);
    const isEstudante =
      user?.tipo === "estudante" ||
      (user?.tipo === "usuario_ciee" && nivel === 1);
    const isCiee = user?.tipo === "usuario_ciee" && nivel >= 2;

    const sections = [];

    if (isEmpresa || isCiee) {
      sections.push({
        id: "empregador",
        section: "Dados do Empregador",
        fields: [
          {
            label: "Razão Social",
            value: formData.razao_social,
            complete: !!formData.razao_social?.trim(),
          },
          {
            label: "CNPJ ou CPF",
            value: formData.cnpj_ou_cpf,
            complete: !!formData.cnpj_ou_cpf?.trim(),
          },
          {
            label: "Representante Legal",
            value: formData.nome_empresa,
            complete: !!formData.nome_empresa?.trim(),
          },
          {
            label: "Cargo do Representante",
            value: formData.cargo_empresa,
            complete: !!formData.cargo_empresa?.trim(),
          },
          {
            label: "Data de início do contrato",
            value: formatDateBR(formData.data_inicio_contrato),
            complete: !!formData.data_inicio_contrato,
          },
          {
            label: "Salário",
            value: formData.salario,
            complete: !!formData.salario && formData.salario !== "R$ 0,00",
          },
        ],
      });
    }

    if (isEstudante || isCiee) {
      const aprendizFields = [
        {
          label: "Nome do Aprendiz",
          value: formData.nome_aprendiz,
          complete: !!formData.nome_aprendiz?.trim(),
        },
        {
          label: "Telefone do Aprendiz",
          value: formData.telefone_contato,
          complete: !!formData.telefone_contato?.trim(),
        },
        {
          label: "E-mail do Aprendiz",
          value: formData.email_aprendiz,
          complete: !!formData.email_aprendiz?.trim(),
        },
        {
          label: "Curso",
          value: formData.curso,
          complete: !!formData.curso?.trim(),
        },
        {
          label: "Atividades do Aprendiz",
          value: formData.atividades_aprendiz,
          complete: !!formData.atividades_aprendiz?.trim(),
        },
      ];
      if (isMenorDeIdade(idadeCalculada)) {
        aprendizFields.push(
          {
            label: "Responsável Legal",
            value: formData.nome_aprendiz_menor_idade,
            complete: !!formData.nome_aprendiz_menor_idade?.trim(),
          },
          {
            label: "Telefone do Responsável",
            value: formData.telefone_aprendiz_menor_idade,
            complete: !!formData.telefone_aprendiz_menor_idade?.trim(),
          },
          {
            label: "E-mail do Responsável",
            value: formData.email_aprendiz_menor_idade,
            complete: !!formData.email_aprendiz_menor_idade?.trim(),
          },
          {
            label: "CPF do Responsável",
            value: formData.cpf_aprendiz_menor_idade,
            complete: !!formData.cpf_aprendiz_menor_idade?.trim(),
          },
        );
      }
      sections.push({
        id: "aprendiz",
        section: "Dados do Aprendiz",
        fields: aprendizFields,
      });
    }

    sections.push({
      id: "local",
      section: "Local de Atividade",
      fields: [
        {
          label: "Local de realização das atividades",
          value:
            localAtividade === "MATRIZ" ? "Matriz" : localAtividade || "Matriz",
          complete: true,
        },
      ],
    });

    return sections;
  };

  const buildMissingFields = () => {
    // Regra para o TCA comum: TODOS os campos do formData são obrigatórios,
    // com EXCEÇÃO das observações (observacoes1 e observacoes2).
    // A estrutura role-aware antiga foi removida em favor dessa regra
    // global, que vale para empresa, estudante e CIEEMG.
    const items = [];

    const CAMPOS_SEMPRE_OPCIONAIS_TCA_COMUM = ["observacoes1", "observacoes2"];

    // Filial: só exigidos se o usuário selecionou "Filial" no select
    // local_realizacao_atividades. Caso contrário ficam liberados.
    const CAMPOS_FILIAL_TCA_COMUM = [
      "nome_filial",
      "cpfCnpj",
      "telefones",
      "telefones2",
      "bairro",
      "municipio",
      "uf",
      "emailContratos",
      "emailFinanceiro",
      "representadaPor",
      "cargoRepresentante",
      "emailRepresentante",
      "responsavelSupervisor",
      "cargoSupervisor",
      "emailSupervisor",
    ];

    const isFilialAtiva =
      String(formData.local_realizacao_atividades || "").toUpperCase() ===
      "FILIAL";

    // Campos do responsável legal (aprendiz menor de idade): exigidos
    // apenas se a idade calculada for < 18.
    const CAMPOS_RESPONSAVEL_MENOR_TCA = [
      "nome_aprendiz_menor_idade",
      "telefone_aprendiz_menor_idade",
      "email_aprendiz_menor_idade",
      "cpf_aprendiz_menor_idade",
    ];
    const menor = isMenorDeIdade(idadeCalculada);

    // "Local de execução diferente": só exigidos quando o usuário
    // MARCOU a checkbox `local_execucao_diferente === true`. Caso
    // contrário, a seção fica oculta na UI e os campos não devem
    // ser cobrados.
    const CAMPOS_LOCAL_EXECUCAO_DIFERENTE = [
      "emp_local_razao_social",
      "emp_local_cnpj",
      "emp_local_telefone",
      "emp_local_email",
      "emp_local_endereco",
      "emp_local_bairro",
      "emp_local_cidade",
      "emp_local_uf",
      "emp_local_cep",
      "emp_local_rep_nome",
      "emp_local_rep_cargo",
      "emp_local_rep_telefone",
      "emp_local_rep_email",
      "resp_admin_nome",
      "resp_admin_cargo",
      "resp_admin_telefone",
      "resp_admin_email",
    ];
    const localExecucaoDiferenteAtivo =
      formData.local_execucao_diferente === true ||
      formData.local_execucao_diferente === 1;

    const labelFor = (key) => {
      // Mapeamento opcional de labels amigáveis. Se não houver, usa o próprio key.
      const labels = {
        empresa_id: "ID da Empresa",
        razao_social: "Razão Social",
        cnpj_ou_cpf: "CNPJ ou CPF",
        email_empresa: "E-mail da Empresa",
        nome_empresa: "Nome do representante legal",
        cargo_empresa: "Cargo do representante",
        telefone_empresa_primario: "Telefone principal da empresa",
        telefone_empresa: "Telefone da empresa",
        data_inicio_contrato: "Data início do contrato",
        data_fim_contrato: "Data fim do contrato",
        salario: "Salário",
        tutor_aprendiz_empresa: "Tutor do aprendiz na empresa",
        cargo_tutor: "Cargo do tutor",
        nome_tutor: "Nome do tutor",
        email_tutor: "E-mail do tutor",
        email_empresa_contratante: "E-mail da empresa contratante",
        endereco_atividade_teorica: "Endereço da atividade teórica",
        regiao: "Região",
        endereco_tutor: "Endereço do tutor",
        carga_horario_curso: "Carga horária do curso",
        curso: "Curso",
        nome_aprendiz: "Nome completo do aprendiz",
        data_nascimento: "Data de nascimento",
        telefone_contato: "Telefone do aprendiz",
        email_aprendiz: "E-mail do aprendiz",
        atividades_aprendiz: "Atividades do aprendiz",
        local_realizacao_atividades: "Local de realização das atividades",
        horario_inicio_horas: "Hora de início (horas)",
        horario_inicio_minutos: "Hora de início (minutos)",
        horario_fim_horas: "Hora de fim (horas)",
        horario_fim_minutos: "Hora de fim (minutos)",
        local_execucao_diferente: "Local de execução diferente?",
        emp_local_razao_social: "Razão social do local de execução",
        emp_local_cnpj: "CNPJ do local de execução",
        emp_local_telefone: "Telefone do local de execução",
        emp_local_email: "E-mail do local de execução",
        emp_local_endereco: "Endereço do local de execução",
        emp_local_bairro: "Bairro do local de execução",
        emp_local_cidade: "Cidade do local de execução",
        emp_local_uf: "UF do local de execução",
        emp_local_cep: "CEP do local de execução",
        emp_local_rep_nome: "Nome do representante do local",
        emp_local_rep_cargo: "Cargo do representante do local",
        emp_local_rep_telefone: "Telefone do representante do local",
        emp_local_rep_email: "E-mail do representante do local",
        resp_admin_nome: "Nome do responsável administrativo",
        resp_admin_cargo: "Cargo do responsável administrativo",
        resp_admin_telefone: "Telefone do responsável administrativo",
        resp_admin_email: "E-mail do responsável administrativo",
        nome_filial: "Nome da filial",
        cpfCnpj: "CNPJ/CPF da filial",
        telefones: "Telefone da filial",
        telefones2: "Telefone 2 da filial",
        bairro: "Bairro da filial",
        municipio: "Município da filial",
        uf: "UF da filial",
        emailContratos: "E-mail de contratos",
        emailFinanceiro: "E-mail financeiro",
        representadaPor: "Representada por",
        cargoRepresentante: "Cargo do representante",
        emailRepresentante: "E-mail do representante",
        responsavelSupervisor: "Responsável/Supervisor",
        cargoSupervisor: "Cargo do supervisor",
        emailSupervisor: "E-mail do supervisor",
        nome_aprendiz_menor_idade: "Nome do responsável legal",
        telefone_aprendiz_menor_idade: "Telefone do responsável legal",
        email_aprendiz_menor_idade: "E-mail do responsável legal",
        cpf_aprendiz_menor_idade: "CPF do responsável legal",
      };
      return labels[key] || key;
    };

    const isEmpty = (v) => {
      if (v === null || v === undefined) return true;
      if (typeof v === "string" && v.trim() === "") return true;
      if (typeof v === "boolean") return false; // boolean não é "vazio"
      return false;
    };

    Object.keys(formData).forEach((key) => {
      // 1) Sempre opcionais
      if (CAMPOS_SEMPRE_OPCIONAIS_TCA_COMUM.includes(key)) return;

      // 2) Filial: só quando local_realizacao_atividades === "FILIAL"
      if (!isFilialAtiva && CAMPOS_FILIAL_TCA_COMUM.includes(key)) {
        return;
      }

      // 3) Responsável legal (menor de idade): só se for menor
      if (!menor && CAMPOS_RESPONSAVEL_MENOR_TCA.includes(key)) {
        return;
      }

      // 4) Campos do "Local de execução diferente": só exigidos
      //    quando a checkbox está marcada. Caso contrário, os campos
      //    ficam ocultos na UI.
      if (
        !localExecucaoDiferenteAtivo &&
        CAMPOS_LOCAL_EXECUCAO_DIFERENTE.includes(key)
      ) {
        return;
      }

      // 5) A própria flag `local_execucao_diferente` é boolean,
      //    não exige valor.
      if (key === "local_execucao_diferente") return;

      if (isEmpty(formData[key])) {
        items.push({ id: key, label: labelFor(key) });
      }
    });

    return items;
  };

  const openReview = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (e && e.stopPropagation) e.stopPropagation();

    const missingFields = buildMissingFields();
    if (missingFields.length > 0) {
      setMissingFieldsAlert(missingFields);
      setShowReview(false);
      return;
    }

    const anexosFaltando = getAnexosObrigatoriosFaltandoTcaComum();
    if (anexosFaltando.length > 0) {
      setMissingFieldsAlert(
        dedupeMissingItems(
          anexosFaltando.map((doc) => ({
            id: doc.id,
            label: doc.label,
          })),
        ),
      );
      toastWarning(
        `Anexos obrigatórios pendentes: ${anexosFaltando
          .map((d) => d.label)
          .join(", ")}.`,
      );
      setShowReview(false);
      return;
    }

    if (formData.data_inicio_contrato && formData.data_fim_contrato) {
      const inicioParts = formData.data_inicio_contrato.split("-");
      const fimParts = formData.data_fim_contrato.split("-");

      if (inicioParts.length === 3 && fimParts.length === 3) {
        const inicio = new Date(
          parseInt(inicioParts[0], 10),
          parseInt(inicioParts[1], 10) - 1,
          parseInt(inicioParts[2], 10),
        );
        const fim = new Date(
          parseInt(fimParts[0], 10),
          parseInt(fimParts[1], 10) - 1,
          parseInt(fimParts[2], 10),
        );

        const limite2Anos = new Date(
          inicio.getFullYear() + 2,
          inicio.getMonth(),
          inicio.getDate(),
        );

        if (fim > limite2Anos) {
          toastError(
            "A data de vigência final não pode ultrapassar 2 anos a partir do início.",
          );
          setShowReview(false);
          return;
        }

        if (formData.data_nascimento) {
          const nascParts = formData.data_nascimento
            .substring(0, 10)
            .split("-");
          if (nascParts.length === 3) {
            const nasc = new Date(
              parseInt(nascParts[0], 10),
              parseInt(nascParts[1], 10) - 1,
              parseInt(nascParts[2], 10),
            );
            const limiteIdade = new Date(
              nasc.getFullYear() + 24,
              nasc.getMonth(),
              nasc.getDate(),
            );
            limiteIdade.setDate(limiteIdade.getDate() - 1);

            if (fim > limiteIdade) {
              toastError(
                "O contrato deve ser encerrado antes de o aprendiz completar 24 anos.",
              );
              setShowReview(false);
              return;
            }
          }
        }
      }
    }

    setMissingFieldsAlert([]);
    setShowReview(true);
  };

  useEffect(() => {
    const idade = calcularIdade(formData.data_nascimento);
    setIdadeCalculada(idade);

    if (idade !== null && idade >= 18) {
      setFormData((prev) => {
        const temAlgoMenor =
          prev.nome_aprendiz_menor_idade ||
          prev.telefone_aprendiz_menor_idade ||
          prev.email_aprendiz_menor_idade ||
          prev.cpf_aprendiz_menor_idade;
        if (!temAlgoMenor) return prev;
        return {
          ...prev,
          nome_aprendiz_menor_idade: "",
          telefone_aprendiz_menor_idade: "",
          email_aprendiz_menor_idade: "",
          cpf_aprendiz_menor_idade: "",
        };
      });
    }
  }, [formData.data_nascimento]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token || !user) {
      window.location.href = "/";
      return;
    }

    const nivel = Number(user.acesso_autorizacao ?? 0);
    const niveisPermitidos = [0, 1, 2, 3, 4];
    if (!niveisPermitidos.includes(nivel)) {
      window.location.href = "/sem-permissao";
      return;
    }

    setFormData((prev) => ({ ...prev, empresa_id: user.empresa_id }));
    setPermissao({
      podeEditarEmpresa: [0, 2, 3, 4].includes(nivel),
      podeEditarEstudante: [1, 2, 3, 4].includes(nivel),
      podeEditarTudo: [2, 3, 4].includes(nivel),
      podeAprovar: [3, 4].includes(nivel),
      somenteLeituraEstudante: [0].includes(nivel),
    });
    setAutorizado(true);

    if (carregouEmpresaRef.current) return;
    carregouEmpresaRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const tcaIdUrl = params.get("id");

    if (user.empresa_id && !tcaIdUrl) {
      axios
        .get(`${API_BASE_URL}/tca/dados-empresa`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((response) => {
          setFormData((prev) => ({
            ...prev,
            empresa_id: response.data.empresa_id,
            razao_social: response.data.razao_social || "",
            cnpj_ou_cpf: formatarCnpjCpf(response.data.cnpj_ou_cpf) || "",
            telefone_empresa_primario: sanitizeTelefone(
              response.data.telefone_empresa_primario ||
                response.data.telefone ||
                "",
            ),
            email_empresa:
              response.data.email || response.data.email_empresa || "",
            regiao: response.data.regiao || "",
            endereco_atividade_teorica:
              response.data.endereco_atividade_teorica || "",
          }));
          if (response.data.endereco_atividade_teorica) {
            matrizEnderecoTeoricoRef.current =
              response.data.endereco_atividade_teorica;
          }
        })
        .catch(console.error);
    }

    if (user.empresa_id) {
      axios
        .get(`${API_BASE_URL}/tca/filiais`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((response) => {
          if (response.data?.possuiFiliais) {
            setFiliais(response.data.opcoes);
            setMostrarFiliais(true);
          }
        })
        .catch(console.error);
    }

    if (tcaIdUrl) {
      axios
        .get(`${API_BASE_URL}/tca/${tcaIdUrl}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((response) => {
          const camposTelefone = [
            "telefone_empresa_primario",
            "telefone_empresa",
            "telefone_contato",
            "telefone_tutor",
            "telefone_aprendiz_menor_idade",
            "emp_local_telefone",
            "emp_local_rep_telefone",
            "resp_admin_telefone",
          ];
          const camposData = [
            "data_inicio_contrato",
            "data_fim_contrato",
            "data_nascimento",
            "data_emissao_rg",
            "data_emissao_titulo",
            "data_emissao_certificado",
          ];
          const dadosSanitizados = { ...response.data };
          camposTelefone.forEach((c) => {
            dadosSanitizados[c] = sanitizeTelefone(dadosSanitizados[c]);
          });

          camposData.forEach((c) => {
            if (dadosSanitizados[c]) {
              dadosSanitizados[c] = toDateInputValue(dadosSanitizados[c]);
            }
          });

          // Empresa (nivel === 0) não deve ver Vigência Final preenchida.
          if (Number(user.acesso_autorizacao) === 0) {
            dadosSanitizados.data_fim_contrato = "";
          }

          const enderecoTeoricaDoBanco =
            response.data.endereco_teorica ||
            response.data.endereco_atividade_teorica ||
            "";
          setFormData((prev) => ({
            ...prev,
            ...dadosSanitizados,
            cnpj_ou_cpf: formatarCnpjCpf(response.data.cnpj_ou_cpf) || "",
            id: response.data.id,
            endereco_atividade_teorica:
              prev.endereco_atividade_teorica || enderecoTeoricaDoBanco,
          }));
          if (enderecoTeoricaDoBanco) {
            matrizEnderecoTeoricoRef.current = enderecoTeoricaDoBanco;
          }
          if (response.data.local_realizacao_atividades) {
            setLocalAtividade(
              String(response.data.local_realizacao_atividades),
            );
          }

          setAnexosFiles((prevFiles) =>
            hydrateAnexosFilesFromPayload({
              schema: ANEXOS_SCHEMA_TCA_COMUM,
              payload: response.data,
              prev: prevFiles,
            }),
          );

          if (Array.isArray(response.data?.historico)) {
            setTcaHistorico(response.data.historico);
          }
        })
        .catch((err) => {
          console.error("Erro ao carregar TCA:", err);
          toastError("Erro ao carregar TCA.");
        });
    } else if (nivel === 1) {
      axios
        .get(`${API_BASE_URL}/tca/me`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((response) => {
          if (response.data?.id) {
            debugLog(
              `[TcaComum] Auto-redirect: /tca/me retornou TCA #${response.data.id}`,
            );
            window.location.href = `/tca?id=${response.data.id}`;
          }
        })
        .catch((err) => {
          if (err.response?.status === 404) {
            debugLog(
              "[TcaComum] Estudante sem TCA ainda — mostrando Aguardando Empresa",
            );
          } else {
            debugLog("[TcaComum] Erro no /tca/me:", err.message);
          }
        });
    }
  }, []);

  const [datasVigenciaFuturas, setDatasVigenciaFuturas] = useState([]);
  const [cursosFiltrados, setCursosFiltrados] = useState([]);
  useEffect(() => {
    const token = localStorage.getItem("token");
    const authHeader = { Authorization: `Bearer ${token}` };

    axios
      .get(`${API_BASE_URL}/tca-configuracoes/datas-vigencia/futuras`, {
        headers: authHeader,
      })
      .then((response) => {
        const ordenadas = (response.data || []).slice().sort((a, b) => {
          const da = a?.valorObj?.data || "";
          const db = b?.valorObj?.data || "";
          return da.localeCompare(db);
        });
        setDatasVigenciaFuturas(ordenadas);
      })
      .catch((err) =>
        logEndpointError(
          "datas de vigência",
          err,
          "/tca-configuracoes/datas-vigencia/futuras",
        ),
      );
    axios
      .get(`${API_BASE_URL}/admin-unidades`, { headers: authHeader })
      .then((response) => setUnidadesCieemg(response.data || []))
      .catch((err) =>
        logEndpointError("unidades cieemg", err, "/admin-unidades"),
      );
    axios
      .get(`${API_BASE_URL}/tca-configuracoes/horarios-teoricos`, {
        headers: authHeader,
      })
      .then((response) => {
        setHorariosTeoricos(response.data || []);
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 404 || status === 405) return;
        console.warn(
          "[TCA] Falha ao carregar horários teóricos (/tca-configuracoes/horarios-teoricos):",
          status || "sem status",
          err?.response?.data || err?.message,
        );
      });
    axios
      .get(`${API_BASE_URL}/tca-configuracoes/cursos`, { headers: authHeader })
      .then((response) => {
        setCursosFiltrados(response.data || []);
      })
      .catch((err) =>
        logEndpointError("cursos", err, "/tca-configuracoes/cursos"),
      );
    axios
      .get(`${API_BASE_URL}/tca-configuracoes/salario-minimo`, {
        headers: authHeader,
      })
      .then((response) => {
        let valorEncontrado = null;
        if (
          response.data &&
          response.data.vigente &&
          response.data.vigente.valor !== undefined
        ) {
          valorEncontrado = response.data.vigente.valor;
        } else if (Array.isArray(response.data) && response.data.length > 0) {
          valorEncontrado = response.data[response.data.length - 1].valor;
        } else if (response.data && response.data.valor !== undefined) {
          valorEncontrado = response.data.valor;
        }
        if (valorEncontrado) setSalarioMinimoVigente(Number(valorEncontrado));
      })
      .catch((err) =>
        logEndpointError(
          "salário mínimo",
          err,
          "/tca-configuracoes/salario-minimo",
        ),
      );
  }, []);

  useEffect(() => {
    // Não sobrescreve se o usuário já editou o campo manualmente.
    if (enderecoTeoricaEditadoManualmenteRef.current) return;

    const local = formData.local_realizacao_atividades || "";

    if (local === "") {
      return;
    }

    if (local === "MATRIZ") {
      let enderecoMatriz = "";
      if (formData.regiao && unidadesCieemg.length > 0) {
        const unidadeEncontrada = unidadesCieemg.find(
          (u) =>
            Number(u.codigo) === Number(formData.regiao) ||
            String(u.codigo) === String(formData.regiao) ||
            String(u.id) === String(formData.regiao) ||
            String(u.codigo_unidade) === String(formData.regiao),
        );

        if (unidadeEncontrada) {
          enderecoMatriz = unidadeEncontrada.endereco;
        }
      }

      if (!enderecoMatriz && matrizEnderecoTeoricoRef.current) {
        enderecoMatriz = matrizEnderecoTeoricoRef.current;
      }

      if (!enderecoMatriz) return;

      setFormData((prev) => ({
        ...prev,
        endereco_atividade_teorica: enderecoMatriz,
      }));
      return;
    }

    // Quando o usuário escolhe uma filial específica (id numérico),
    // o endereço teórico vem do `unidadeTeorica.endereco` retornado
    // pela API /filiais/:id (já carregado em `dadosFilialSelecionada`).
    if (local !== "MATRIZ" && local !== "FILIAL" && !isNaN(Number(local))) {
      const enderecoTeoricaFilial =
        dadosFilialSelecionada?.unidadeTeorica?.endereco || "";
      if (!enderecoTeoricaFilial) return;
      setFormData((prev) => ({
        ...prev,
        endereco_atividade_teorica: enderecoTeoricaFilial,
      }));
    }
  }, [
    formData.regiao,
    unidadesCieemg,
    formData.local_realizacao_atividades,
    dadosFilialSelecionada,
  ]);

  const isEmpresaNivel0 = Number(user?.acesso_autorizacao ?? -1) === 0;

  // Auto-preenchimento do endereço da aprendizagem PRÁTICA (endereco_tutor)
  // baseado em Matriz / Filial / local de execução informado pela empresa.
  // Funciona para todos os perfis, mas só sobrescreve se o usuário ainda
  // não tiver editado o campo manualmente.
  useEffect(() => {
    if (enderecoTutorEditadoManualmenteRef.current) return;

    let enderecoCalculado = "";

    if (formData.local_execucao_diferente) {
      // Quando o local de execução é diferente da empresa,
      // o endereço é o do próprio local de execução.
      enderecoCalculado = formData.emp_local_endereco || "";
    } else if (localAtividade === "MATRIZ") {
      // Quando a aprendizagem prática ocorre na matriz.
      enderecoCalculado = matrizEnderecoTeoricoRef.current || "";
    } else if (
      localAtividade &&
      localAtividade !== "MATRIZ" &&
      localAtividade !== "FILIAL" &&
      !isNaN(Number(localAtividade))
    ) {
      // Quando o usuário escolheu uma filial específica.
      enderecoCalculado = dadosFilialSelecionada?.endereco || "";
    }

    if (!enderecoCalculado) return;

    setFormData((prev) =>
      prev.endereco_tutor === enderecoCalculado
        ? prev
        : { ...prev, endereco_tutor: enderecoCalculado },
    );
  }, [
    formData.local_execucao_diferente,
    formData.emp_local_endereco,
    localAtividade,
    dadosFilialSelecionada,
  ]);

  useEffect(() => {
    const filialId = String(localAtividade || "").trim();
    if (!filialId || filialId === "MATRIZ" || isNaN(Number(filialId))) {
      setDadosFilialSelecionada(null);
      return;
    }

    let cancelled = false;
    const carregarFilial = async () => {
      setFilialLoading(true);
      try {
        const token = localStorage.getItem("token");
        const url = API_ENDPOINTS.filial_id(filialId);
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;

        const dados = response.data || {};

        setDadosFilialSelecionada(dados);

        const mapeamento = [
          ["nome_filial", dados.nome_filial],
          ["cpfCnpj", dados.cnpj],
          ["cepFilial", dados.cep],
          ["enderecoFilial", dados.endereco],
          ["telefones", dados.telefone_1],
          ["telefones2", dados.telefone_2],
          [
            "endereco_atividade_teorica",
            dados.unidadeTeorica ? dados.unidadeTeorica.endereco : "",
          ],
          ["bairro", dados.bairro],
          ["municipio", dados.cidade],
          ["uf", dados.uf],
          ["emailContratos", dados.email_contratos],
          ["emailFinanceiro", dados.email_financeiro],
          ["representadaPor", dados.representante_nome],
          ["cargoRepresentante", dados.representante_cargo],
          ["emailRepresentante", dados.representante_email],
          ["responsavelSupervisor", dados.supervisor_nome],
          ["cargoSupervisor", dados.supervisor_cargo],
          ["emailSupervisor", dados.supervisor_email],
        ];

        setFormData((prev) => {
          const next = { ...prev };
          mapeamento.forEach(([campo, valor]) => {
            if (valor !== undefined && valor !== null) {
              next[campo] = valor;
            }
          });
          return next;
        });

        toastSuccess("Dados da filial carregados.");
      } catch (err) {
        if (!cancelled) toastError("Erro ao buscar detalhes da filial.");
      } finally {
        if (!cancelled) setFilialLoading(false);
      }
    };
    carregarFilial();
    return () => {
      cancelled = true;
    };
  }, [localAtividade]);

  useEffect(() => {
    const municipio = formData.local_execucao_diferente
      ? formData.emp_local_cidade
      : formData.cidade;
    if (municipio) {
      const token = localStorage.getItem("token");
      const url = `${API_BASE_URL}/tca-configuracoes/cursos/municipio/${encodeURIComponent(municipio)}`;
      axios
        .get(url, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((r) => {
          const cursosMunicipio = r.data || [];
          if (cursosMunicipio.length === 0) return;
          setCursosFiltrados((prev) => {
            const ids = new Set(prev.map((c) => c.id));
            const novos = cursosMunicipio.filter((c) => !ids.has(c.id));
            return [...prev, ...novos];
          });
        })
        .catch((err) =>
          logEndpointError(
            "filtro de cursos por município",
            err,
            "/tca-configuracoes/cursos/municipio/{municipio}",
          ),
        );
    }
  }, [
    formData.cidade,
    formData.emp_local_cidade,
    formData.local_execucao_diferente,
  ]);

  const findCurso = (cursoVal) => {
    if (!cursoVal) return null;
    return cursosFiltrados.find((c) => {
      const fullDesc = `${c.valorObj.curso} — ${c.valorObj.carga_horaria} (${c.valorObj.modalidade})`;
      const cursoModalidade = `${c.valorObj.curso} (${c.valorObj.modalidade})`;
      return (
        fullDesc === cursoVal ||
        cursoModalidade === cursoVal ||
        c.valorObj.curso === cursoVal ||
        String(c.id) === String(cursoVal)
      );
    });
  };

  const [atividadesFiltradas, setAtividadesFiltradas] = useState([]);
  useEffect(() => {
    if (!formData.curso) {
      setAtividadesFiltradas([]);
      setFormData((prev) => ({ ...prev, atividades_aprendiz: "" }));
      return;
    }

    const token = localStorage.getItem("token");
    const authHeader = { Authorization: `Bearer ${token}` };

    const ensureCursos = async () => {
      if (cursosFiltrados.length > 0) return cursosFiltrados;
      const r = await axios.get(`${API_BASE_URL}/tca-configuracoes/cursos`, {
        headers: authHeader,
      });
      const cursos = r.data || [];
      setCursosFiltrados(cursos);
      return cursos;
    };

    const resolveCursoId = (cursos) => {
      const achou = cursos.find((c) => {
        const fullDesc = `${c.valorObj.curso} — ${c.valorObj.carga_horaria} (${c.valorObj.modalidade})`;
        const cursoModalidade = `${c.valorObj.curso} (${c.valorObj.modalidade})`;
        return (
          fullDesc === formData.curso ||
          cursoModalidade === formData.curso ||
          c.valorObj.curso === formData.curso ||
          String(c.id) === String(formData.curso)
        );
      });
      return achou?.id;
    };

    (async () => {
      try {
        const cursos = await ensureCursos();
        const cursoId = resolveCursoId(cursos);
        if (!cursoId) {
          setAtividadesFiltradas([]);
          return;
        }
        let atividades = [];
        try {
          const r = await axios.get(
            `${API_BASE_URL}/tca-configuracoes/atividades?curso_id=${encodeURIComponent(cursoId)}`,
            { headers: authHeader },
          );
          atividades = r.data || [];
        } catch (err) {
          if (err?.response?.status !== 404) throw err;
          try {
            const r2 = await axios.get(
              `${API_BASE_URL}/tca-configuracoes/atividades`,
              { headers: authHeader },
            );
            const todas = r2.data || [];
            atividades = todas.filter(
              (a) => String(a.curso_id) === String(cursoId),
            );
          } catch (err2) {
            if (err2?.response?.status !== 404) throw err2;
            atividades = [];
          }
        }
        setAtividadesFiltradas(atividades);
      } catch (err) {
        console.warn("Erro ao carregar atividades:", err?.message);
        setAtividadesFiltradas([]);
      }
    })();
  }, [formData.curso, cursosFiltrados]);

  const [erroHorarioAprendizagem, setErroHorarioAprendizagem] = useState(null);
  const [horarioBloqueado, setHorarioBloqueado] = useState({
    horario_inicio_horas: false,
    horario_inicio_minutos: false,
    horario_fim_horas: false,
    horario_fim_minutos: false,
  });

  const filtrarHorarioParcial = (campo, valorAtual, valorDigitado) => {
    if (formData.carga_horario_curso !== "4 horas diárias") {
      return { valor: valorDigitado, bloqueado: false };
    }

    const soDigitos = String(valorDigitado).replace(/\D/g, "");
    if (!soDigitos) return { valor: "", bloqueado: false };

    const valoresValidos = {
      horario_inicio_horas: ["07", "13"],
      horario_inicio_minutos: ["30"],
      horario_fim_horas: ["11", "17"],
      horario_fim_minutos: ["30"],
    };

    const permitidos = valoresValidos[campo] || [];
    const valorAtualLimpo = String(valorAtual || "").replace(/\D/g, "");

    let novoValor = "";
    let tentouInvalido = false;

    for (const digito of soDigitos) {
      const tentativa = novoValor + digito;

      const aindaValido = permitidos.some(
        (v) => v.startsWith(tentativa) || tentativa.startsWith(v),
      );

      if (!aindaValido) {
        tentouInvalido = true;
        continue;
      }

      const algumPermiteExato = permitidos.includes(tentativa);
      const algumPermitePrefixo = permitidos.some((v) =>
        v.startsWith(tentativa),
      );

      if (algumPermiteExato) {
        novoValor = tentativa;
      } else if (algumPermitePrefixo) {
        novoValor = tentativa;
      } else {
        tentouInvalido = true;
      }
    }

    novoValor = novoValor.slice(0, 2);

    if (
      novoValor.length === 2 &&
      !permitidos.includes(novoValor) &&
      !permitidos.some((v) => v.startsWith(novoValor))
    ) {
      novoValor = valorAtualLimpo.slice(0, 2);
      tentouInvalido = true;
    }

    return { valor: novoValor, bloqueado: tentouInvalido };
  };

  useEffect(() => {
    const erro = validarHorarioAprendizagem();
    setErroHorarioAprendizagem(erro);

    if (erro === null) {
      if (
        formData.curso &&
        formData.carga_horario_curso &&
        formData.horario_inicio_horas &&
        formData.horario_inicio_minutos &&
        formData.horario_fim_horas &&
        formData.horario_fim_minutos &&
        !horarioValidoAlertadoRef.current
      ) {
        toastSuccess(
          "Horário da aprendizagem prática preenchido corretamente!",
        );
        horarioValidoAlertadoRef.current = true;
      }
    } else {
      horarioValidoAlertadoRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.curso,
    formData.carga_horario_curso,
    formData.horario_inicio_horas,
    formData.horario_inicio_minutos,
    formData.horario_fim_horas,
    formData.horario_fim_minutos,
    cursosFiltrados,
  ]);

  useEffect(() => {
    debugLog(
      "[DEBUG AUTO-SELECT] curso =",
      formData.curso,
      "cursosFiltradosLength =",
      cursosFiltrados.length,
    );
    if (formData.curso && cursosFiltrados.length > 0) {
      const cursoObj = findCurso(formData.curso);
      debugLog("[DEBUG AUTO-SELECT] found cursoObj =", cursoObj);
      if (cursoObj && cursoObj.valorObj.carga_horaria) {
        const ch = String(cursoObj.valorObj.carga_horaria).replace(/\D/g, "");
        debugLog("[DEBUG AUTO-SELECT] ch =", ch);
        let targetHours = "";
        if (ch === "4" || ch === "1936" || ch === "1900") {
          targetHours = "4 horas diárias";
        } else if (ch === "6" || ch === "2230" || ch === "2000") {
          targetHours = "6 horas diárias";
        } else {
          const numHours = parseInt(ch, 10);
          if (!isNaN(numHours)) {
            targetHours =
              numHours <= 1936 ? "4 horas diárias" : "6 horas diárias";
          }
        }
        debugLog("[DEBUG AUTO-SELECT] targetHours =", targetHours);
        if (targetHours && formData.carga_horario_curso !== targetHours) {
          debugLog(
            "[DEBUG AUTO-SELECT] updating carga_horario_curso to",
            targetHours,
          );
          setFormData((prev) => ({
            ...prev,
            carga_horario_curso: targetHours,
          }));
        }
      }
    }
  }, [formData.curso, cursosFiltrados]);

  const prevSalarioCalculadoRef = useRef(null);

  useEffect(() => {
    debugLog(
      "[DEBUG CALCULATION] carga_horario_curso =",
      formData.carga_horario_curso,
      "salarioMinimoVigente =",
      salarioMinimoVigente,
    );
    let horasDiarias = 0;
    if (formData.carga_horario_curso === "4 horas diárias") {
      horasDiarias = 4;
    } else if (formData.carga_horario_curso === "6 horas diárias") {
      horasDiarias = 6;
    }

    debugLog("[DEBUG CALCULATION] horasDiarias =", horasDiarias);

    if (horasDiarias === 4 || horasDiarias === 6) {
      const valorHora = Math.round((salarioMinimoVigente / 220) * 100) / 100;
      const horasSemanais = horasDiarias * 5;
      const valorSemanal = valorHora * horasSemanais;
      const salarioCalculado =
        Math.round(((valorSemanal * 4.4285 * 7) / 6) * 100) / 100;

      const currentSal = parseSalario(formData.salario);
      debugLog(
        "[DEBUG CALCULATION] currentSal =",
        currentSal,
        "salarioCalculado =",
        salarioCalculado,
      );

      if (
        !currentSal ||
        currentSal < salarioCalculado ||
        currentSal === prevSalarioCalculadoRef.current
      ) {
        debugLog("[DEBUG CALCULATION] updating salario to", salarioCalculado);
        setFormData((prev) => ({
          ...prev,
          salario: salarioCalculado,
        }));
      }

      prevSalarioCalculadoRef.current = salarioCalculado;
    }
  }, [formData.carga_horario_curso, salarioMinimoVigente]);

  const buscarDadosDoAprendiz = async (cpfDigitado) => {
    if (!cpfDigitado) return;
    const cpfLimpo = String(cpfDigitado).replace(/\D/g, "");

    if (cpfLimpo.length !== 11) return;

    try {
      setEstudanteNaoEncontrado(false);
      setCienteCadastro(false);
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/estudantes/cpf/${cpfLimpo}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const dadosDoEstudante = response.data;
      console.log(
        "=== DADOS DO ESTUDANTE RETORNADOS PELA API ===",
        dadosDoEstudante,
      );

      if (dadosDoEstudante) {
        const cpfMaeBruto =
          dadosDoEstudante.cpf_mae ||
          dadosDoEstudante.cpfmae ||
          dadosDoEstudante.cpf_responsavel_legal ||
          "";
        const cpfMaeLimpo = String(cpfMaeBruto).replace(/\D/g, "");
        const cpfMaeValido = cpfMaeLimpo.length >= 11 ? cpfMaeBruto : "";

        setFormData((prev) => {
          const prevCpf = prev.cpf_aprendiz_menor_idade || "";
          const prevCpfLimpo = prevCpf.replace(/\D/g, "");
          const prevCpfValido = prevCpfLimpo.length >= 11;

          return {
            ...prev,
            nome_aprendiz: dadosDoEstudante.nome_completo || "",
            telefone_contato: dadosDoEstudante.telefone || "",
            email_aprendiz: dadosDoEstudante.email || "",
            data_nascimento:
              dadosDoEstudante.dt_nascimento ||
              dadosDoEstudante.data_nascimento ||
              "",
            nome_aprendiz_menor_idade:
              dadosDoEstudante.nome_mae ||
              dadosDoEstudante.nomemae ||
              dadosDoEstudante.nome_responsavel_legal ||
              dadosDoEstudante.mae ||
              prev.nome_aprendiz_menor_idade ||
              "",
            cpf_aprendiz_menor_idade:
              cpfMaeValido || (!prevCpfValido ? prevCpf : "") || "",
          };
        });
      }
    } catch (error) {
      console.log("Estudante não encontrado ou erro na busca", error);
      setEstudanteNaoEncontrado(true);
      setCienteCadastro(false);
      setFormData((prev) => ({
        ...prev,
        nome_aprendiz: "",
        telefone_contato: "",
        email_aprendiz: "",
        data_nascimento: "",
      }));
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      const cpfValue = formData.cpf_aprendiz || "";
      const cpfLimpo = String(cpfValue).replace(/\D/g, "");

      if (cpfLimpo.length < 11) {
        setEstudanteNaoEncontrado(false);
        if (cpfLimpo.length === 0) {
          setFormData((prev) => {
            if (
              prev.nome_aprendiz ||
              prev.telefone_contato ||
              prev.email_aprendiz ||
              prev.data_nascimento
            ) {
              return {
                ...prev,
                nome_aprendiz: "",
                telefone_contato: "",
                email_aprendiz: "",
                data_nascimento: "",
              };
            }
            return prev;
          });
        }
        return;
      }

      buscarDadosDoAprendiz(cpfValue);
    }, 1000);
    return () => clearTimeout(timer);
  }, [formData.cpf_aprendiz]);

  const handleChange = (e) => {
    debugLog("CHANGE:", e.target.name, "=", JSON.stringify(e.target.value));
    const { name, value, type, checked } = e.target;
    let newValue = type === "checkbox" ? checked : value;

    if (
      name === "telefone_empresa_primario" ||
      name === "telefone_empresa" ||
      name === "telefone_contato" ||
      name === "telefone_tutor" ||
      name === "telefone_aprendiz_menor_idade" ||
      name === "emp_local_telefone" ||
      name === "emp_local_rep_telefone" ||
      name === "resp_admin_telefone"
    ) {
      newValue = String(newValue || "")
        .replace(/\D/g, "")
        .slice(0, 11);
    }

    if (
      name === "cnpj_ou_cpf" ||
      name === "emp_local_cnpj" ||
      name === "cpf_aprendiz_menor_idade" ||
      name === "cpf_aprendiz"
    ) {
      newValue = formatarCnpjCpf(newValue);
    }

    const emojiRegex = /[\p{Extended_Pictographic}]/gu;
    newValue = newValue.replace(emojiRegex, "");

    const regexLetras = /[^0-9a-zA-ZÀ-ÿ\s.,&\-/]/g;
    const camposLetras = [
      "razao_social",
      "nome_empresa",
      "cargo_empresa",
      "tutor_aprendiz_empresa",
      "cargo_tutor",
      "nome_tutor",
      "endereco_tutor",
      "nome_aprendiz",
      "nome_aprendiz_menor_idade",
      "observacoes1",
      "observacoes2",
    ];
    if (camposLetras.includes(name)) {
      newValue = newValue.replace(regexLetras, "");
    }

    const regexEmail = /[^0-9a-zA-ZÀ-ÿ@._+\-]/g;
    const camposEmail = [
      "email_empresa",
      "email_empresa_contratante",
      "email_tutor",
      "email_aprendiz",
      "email_aprendiz_menor_idade",
      "emp_local_email",
      "emp_local_rep_email",
      "resp_admin_email",
    ];
    if (camposEmail.includes(name)) {
      newValue = newValue.replace(regexEmail, "");
    }

    // Marca os endereços como editados manualmente para impedir que o
    // efeito de auto-preenchimento (Matriz/Filial) sobrescreva o que
    // o usuário acabou de digitar.
    if (name === "endereco_tutor") {
      enderecoTutorEditadoManualmenteRef.current = true;
    } else if (name === "endereco_atividade_teorica") {
      enderecoTeoricaEditadoManualmenteRef.current = true;
    }

    setFormData((prev) => ({ ...prev, [name]: newValue }));

    // Limpa erro de formato de e-mail enquanto o usuário digita.
    if (CAMPOS_EMAIL.includes(name) && emailErrors[name]) {
      setEmailErrors((prev) => ({ ...prev, [name]: false }));
    }
  };

  const getAutorizacaoTCA = async (empresaId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/tca/empresa/${empresaId}/autorizacao`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Erro autorização:", error);
      return null;
    }
  };

  const handleAnexoChange = (key) => (e) => {
    const file = e.target.files[0];

    if (!file) return;

    const allowed = ["application/pdf", "image/png", "image/jpeg"];

    if (!allowed.includes(file.type)) {
      toastWarning("Formato inválido. Use PDF, PNG ou JPG.");
      e.target.value = "";
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toastWarning("Arquivo maior que 10MB.");
      e.target.value = "";
      return;
    }

    setAnexosFiles((prev) => ({ ...prev, [key]: file }));

    setAnexosStatus((prev) => ({ ...prev, [key]: "uploading" }));

    setAnexosProgress((prev) => ({ ...prev, [key]: 0 }));

    try {
      const token = localStorage.getItem("token");
      const fd = new FormData();
      fd.append("file", file);

      axios
        .post(`${API_BASE_URL}/upload`, fd, {
          headers: { Authorization: `Bearer ${token}` },

          onUploadProgress: (ev) => {
            if (ev.total) {
              const real = Math.round((ev.loaded * 100) / ev.total);

              setAnexosProgress((prev) => ({ ...prev, [key]: real }));

              if (real >= 100) {
                setAnexosStatus((prev) => ({ ...prev, [key]: "done" }));
              }
            }
          },
        })
        .then((res) => {
          const foto = res?.data?.foto || res?.data || {};
          if (typeof window !== "undefined") {
            console.log("[TCA upload] response:", res?.status, foto);
          }
          setAnexosFiles((prev) => {
            const prevFile = prev[key];
            const base =
              prevFile instanceof File ? { file: prevFile } : prevFile || {};
            return {
              ...prev,
              [key]: {
                ...base,
                filename: foto.filename,
                originalname: foto.originalname,
                tipo_documento: foto.tipo_documento,
              },
            };
          });
        })
        .catch((err) => {
          console.error("Erro upload:", err);

          setAnexosStatus((prev) => ({ ...prev, [key]: "error" }));
          toastError("Erro ao enviar arquivo. Tente novamente.");
        });
    } catch (err) {
      console.error("Erro inesperado no upload:", err);
    }
  };

  const HORARIOS_DIARIOS = {
    "4 horas diárias": 4,
    "6 horas diárias": 6,
  };

  const HORARIOS_PADRAO_PRATICA = {
    manha4h: { inicio: "07:30", fim: "11:30" },
    tarde4h: { inicio: "13:30", fim: "17:30" },
  };

  const formatarHorario = (horas, minutos) =>
    `${String(horas).padStart(2, "0")}:${String(minutos).padStart(2, "0")}`;

  const validarHorarioAprendizagem = () => {
    const camposVazios =
      !formData.horario_inicio_horas ||
      !formData.horario_inicio_minutos ||
      !formData.horario_fim_horas ||
      !formData.horario_fim_minutos;

    if (camposVazios) return null;

    const horaIni = Number(formData.horario_inicio_horas);
    const minIni = Number(formData.horario_inicio_minutos);
    const horaFim = Number(formData.horario_fim_horas);
    const minFim = Number(formData.horario_fim_minutos);

    if (
      Number.isNaN(horaIni) ||
      Number.isNaN(minIni) ||
      Number.isNaN(horaFim) ||
      Number.isNaN(minFim)
    ) {
      return "Preencha horário de início e fim válidos para a aprendizagem prática.";
    }

    if (horaIni > 23 || minIni > 59 || horaFim > 23 || minFim > 59) {
      return "Horário inválido. Use valores entre 00:00 e 23:59.";
    }

    const inicioMinutos = horaIni * 60 + minIni;
    const fimMinutos = horaFim * 60 + minFim;

    if (fimMinutos <= inicioMinutos) {
      return "O horário final da aprendizagem prática deve ser maior que o horário inicial.";
    }

    const duracaoMinutos = fimMinutos - inicioMinutos;
    const duracaoHoras = duracaoMinutos / 60;
    const inicioFormatado = formatarHorario(horaIni, minIni);
    const fimFormatado = formatarHorario(horaFim, minFim);

    if (formData.carga_horario_curso === "4 horas diárias") {
      const horariosValidos = Object.values(HORARIOS_PADRAO_PRATICA);
      const ehValido = horariosValidos.some(
        (h) => h.inicio === inicioFormatado && h.fim === fimFormatado,
      );

      if (!ehValido) {
        return "Informe o  horário exato referente ao curso: 07:30 às 11:30 ou 13:30 às 17:30.";
      }

      return null;
    }

    const horasDiariasPermitidas =
      HORARIOS_DIARIOS[formData.carga_horario_curso] || 6;

    if (duracaoHoras !== horasDiariasPermitidas) {
      const horasFormatadas = duracaoHoras
        .toFixed(2)
        .replace(".", ",")
        .replace(/,(\d)0$/, ",$1")
        .replace(/,00$/, "");
      return `A duração da aprendizagem prática (${horasFormatadas}h) deve ser exatamente igual a ${horasDiariasPermitidas}h por dia.`;
    }

    return null;
  };

  // Regex de e-mail: usuario@dominio.com(.br)
  // Aceita letras/dígitos/pontos/hífens/underscore no local,
  // subdomínios no domínio e TLD de 2+ letras.
  const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

  // Lista de todos os campos de e-mail do formulário TCA.
  const CAMPOS_EMAIL = [
    "email_empresa",
    "emailContratos",
    "emailFinanceiro",
    "emailRepresentante",
    "emailSupervisor",
    "email_empresa_contratante",
    "emp_local_email",
    "emp_local_rep_email",
    "resp_admin_email",
    "email_aprendiz",
    "email_aprendiz_menor_idade",
    "email_tutor",
  ];

  // Mapa legível (campo → rótulo) pra mensagens de erro mais amigáveis.
  const ROTULO_EMAIL = {
    email_empresa: "E-mail da empresa",
    emailContratos: "E-mail Contrato",
    emailFinanceiro: "E-mail Financeiro",
    emailRepresentante: "E-mail do representante",
    emailSupervisor: "E-mail do responsável/supervisor",
    email_empresa_contratante: "E-mail da empresa contratante",
    emp_local_email: "E-mail do local de execução",
    emp_local_rep_email: "E-mail do representante do local",
    resp_admin_email: "E-mail do responsável administrativo",
    email_aprendiz: "E-mail do aprendiz",
    email_aprendiz_menor_idade: "E-mail do responsável (menor de idade)",
    email_tutor: "E-mail do responsável pelas avaliações",
  };

  // Valida todos os campos de e-mail. Retorna a lista dos que estão
  // preenchidos com formato inválido (vazio = tudo OK).
  const validarEmails = () => {
    const invalidos = [];
    const mapa = {};
    CAMPOS_EMAIL.forEach((campo) => {
      const valor = String(formData[campo] || "").trim();
      if (!valor) {
        mapa[campo] = false;
        return;
      }
      const invalido = !EMAIL_REGEX.test(valor);
      mapa[campo] = invalido;
      if (invalido) {
        invalidos.push({ campo, valor, rotulo: ROTULO_EMAIL[campo] || campo });
      }
    });
    setEmailErrors(mapa);
    return invalidos;
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    const emailsInvalidos = validarEmails();
    if (emailsInvalidos.length > 0) {
      const lista = emailsInvalidos
        .map((e) => `${e.rotulo}: "${e.valor}"`)
        .join("\n• ");
      toastError(`E-mail inválido. Corrija antes de continuar:\n• ${lista}`);
      return;
    }

    const erroHorario = validarHorarioAprendizagem();

    if (erroHorario) {
      toastError(erroHorario);
      return;
    }

    if (estudanteNaoEncontrado) {
      toastError(
        "O contrato não poderá ser validado enquanto o cadastro do(a) aprendiz não for realizado.",
      );
      return;
    }

    const anexosFaltando = getAnexosObrigatoriosFaltandoTcaComum();
    if (anexosFaltando.length > 0) {
      setMissingFieldsAlert(
        dedupeMissingItems(
          anexosFaltando.map((doc) => ({ id: doc.id, label: doc.label })),
        ),
      );
      toastWarning(
        `Anexos obrigatórios pendentes: ${anexosFaltando
          .map((d) => d.label)
          .join(", ")}.`,
      );
      return;
    }

    if (submittingRef.current) return;
    submittingRef.current = true;

    if (
      !permissao.podeEditarEmpresa &&
      !permissao.podeEditarEstudante &&
      !permissao.podeEditarTudo
    ) {
      toastError("Sem permissão para editar.");
      submittingRef.current = false;
      return;
    }

    try {
      let horasDiarias = 0;
      if (formData.carga_horario_curso === "4 horas diárias") {
        horasDiarias = 4;
      } else if (formData.carga_horario_curso === "6 horas diárias") {
        horasDiarias = 6;
      } else if (formData.curso && cursosFiltrados.length > 0) {
        const cursoObj = cursosFiltrados.find(
          (c) => c.valorObj.curso === formData.curso,
        );
        if (cursoObj && cursoObj.valorObj.carga_horaria) {
          const ch = String(cursoObj.valorObj.carga_horaria).replace(/\D/g, "");
          if (ch === "1936") horasDiarias = 4;
          else if (ch === "2230") horasDiarias = 6;
        }
      }

      if (horasDiarias === 4 || horasDiarias === 6) {
        const salarioInformado = parseSalario(formData.salario);
        const erro = validarSalarioMinimo(
          salarioInformado,
          salarioMinimoVigente,
          horasDiarias,
        );
        if (erro) {
          toastError(
            buildMensagemSalarioInferiorMinimo(
              salarioInformado,
              erro.salarioMinimoCalculado,
              erro.horasDiarias,
            ),
          );
          submittingRef.current = false;
          return;
        }
      }

      const token = localStorage.getItem("token");
      const decoded = jwtDecode(token);

      let linkAssinatura =
        "https://na2.docusign.net/Member/PowerFormSigning.aspx?PowerFormId=6f691352-9302-4819-8c46-0fb54f120c57&env=na2&acct=272fb0e3-8eca-4797-ac82-b4ba543c625f&v=2";
      let tipoAutorizacao = null;
      if (formData.empresa_id) {
        try {
          const autorizacao = await getAutorizacaoTCA(formData.empresa_id);
          if (autorizacao?.linkDocuSign)
            linkAssinatura = autorizacao.linkDocuSign;
          else if (autorizacao?.link && autorizacao.link.startsWith("http"))
            linkAssinatura = autorizacao.link;
          tipoAutorizacao =
            autorizacao?.tipoAutorizacao || autorizacao?.tipo || null;
        } catch (err) {
          console.warn("Erro ao buscar autorização:", err);
        }
      }

      const formDataLimpo = { ...formData };
      const telefoneFromState = String(formDataLimpo.telefone_contato || "");
      const telefoneFromDom =
        typeof document !== "undefined" &&
        document.getElementById("telefone_contato")
          ? String(document.getElementById("telefone_contato").value || "")
          : "";
      formDataLimpo.telefone_contato = String(
        telefoneFromState || telefoneFromDom,
      ).replace(/\D/g, "");
      [
        "email_empresa",
        "email_empresa_contratante",
        "email_tutor",
        "email_aprendiz",
        "email_aprendiz_menor_idade",
        "emp_local_email",
        "emp_local_rep_email",
        "resp_admin_email",
      ].forEach((c) => {
        if (formDataLimpo[c]) {
          formDataLimpo[c] = formDataLimpo[c]
            .replace(
              /[\p{Extended_Pictographic}\p{Emoji_Presentation}\uFE0F\u200D\u20E3\u{1F300}-\u{1F9FF}\u{1FA70}-\u{1FAFF}\u{2600}-\u{26FF}]/gu,
              "",
            )
            .replace(/[^0-9a-zA-ZÀ-ÿ@._+\-]/g, "");
        }
      });

      const camposTelefone = [
        "telefone_empresa_primario",
        "telefone_empresa",
        "telefone_contato",
        "telefone_tutor",
        "telefone_aprendiz_menor_idade",
        "emp_local_telefone",

        "emp_local_rep_telefone",
        "resp_admin_telefone",
      ];

      camposTelefone.forEach((c) => {
        if (!formDataLimpo[c]) return;
        const nums = String(formDataLimpo[c]).replace(/\D/g, "").slice(0, 11);
        if (nums.length <= 2) formDataLimpo[c] = nums;
        else if (nums.length <= 6)
          formDataLimpo[c] = `(${nums.slice(0, 2)})${nums.slice(2)}`;
        else if (nums.length <= 10)
          formDataLimpo[c] =
            `(${nums.slice(0, 2)})${nums.slice(2, 6)}-${nums.slice(6)}`;
        else
          formDataLimpo[c] =
            `(${nums.slice(0, 2)})${nums.slice(2, 7)}-${nums.slice(7)}`;
      });

      if (
        !formDataLimpo.telefone_empresa &&
        formDataLimpo.telefone_empresa_primario
      ) {
        formDataLimpo.telefone_empresa =
          formDataLimpo.telefone_empresa_primario;
      }

      const payload = {
        ...formDataLimpo,
        tutor_aprendiz_empresa:
          formDataLimpo.nome_tutor || formDataLimpo.tutor_aprendiz_empresa,
        tipo_programa: Number(decoded.tipo_programa) || 1,
        tipo_autorizacao: tipoAutorizacao,
        link_docusign: linkAssinatura,
        local_realizacao_atividades: localAtividade || "MATRIZ",
        endereco_teorica:
          formDataLimpo.endereco_teorica ||
          formDataLimpo.endereco_atividade_teorica ||
          "",
        ...(() => {
          const patch = buildAnexosObrigatoriosBodyPatch({
            schema: ANEXOS_SCHEMA_TCA_COMUM,
            anexosFiles,
          });
          if (typeof window !== "undefined") {
            console.log("[TCA submit] anexosFiles:", anexosFiles);
            console.log("[TCA submit] anexosPatch:", patch);
          }
          return patch;
        })(),
        anexos_obrigatorios_ids: buildAnexosObrigatoriosIds({
          isEstudante: isStudent,
          schema: ANEXOS_SCHEMA_TCA_COMUM,
          idSuffix: "_path",
        }),
      };

      const response = await axios.post(`${API_BASE_URL}/tca`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const novoId = response.data?.id || response.data?.tcaId || "?";

      const filialIdNum = Number(localAtividade);
      const filialPayload = {
        nome_filial: formData.nome_filial,
        cnpj: formData.cpfCnpj,
        telefone_1: formData.telefones,
        telefone_2: formData.telefones2,
        bairro: formData.bairro,
        cidade: formData.municipio,
        uf: formData.uf,
        email_contratos: formData.emailContratos,
        email_financeiro: formData.emailFinanceiro,
        representante_nome: formData.representadaPor,
        representante_cargo: formData.cargoRepresentante,
        representante_email: formData.emailRepresentante,
        supervisor_nome: formData.responsavelSupervisor,
        supervisor_cargo: formData.cargoSupervisor,
        supervisor_email: formData.emailSupervisor,
      };
      if (
        mostrarFiliais &&
        localAtividade &&
        localAtividade !== "MATRIZ" &&
        !isNaN(filialIdNum)
      ) {
        try {
          await axios.put(
            API_ENDPOINTS.filial_update(filialIdNum),
            filialPayload,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          toastSuccess("Dados da filial atualizados.");
        } catch (filialErr) {
          console.warn(
            "Falha ao persistir filial (PUT /filiais/:id):",
            filialErr,
            filialErr?.response?.data,
          );
          const status = filialErr.response?.status;
          const backendMsg =
            filialErr.response?.data?.message ||
            filialErr.response?.data?.erro ||
            filialErr.response?.data?.error ||
            filialErr.message ||
            "Erro ao atualizar dados da filial";
          if (status === 403) {
            toastError(
              "Sem permissão para atualizar a filial. Contate o administrador do sistema.",
            );
          } else if (status === 401) {
            toastError(
              "Sessão expirada. Faça login novamente para atualizar a filial.",
            );
          } else if (status === 404) {
            toastError("Filial não encontrada no servidor.");
          } else if (status >= 500) {
            toastError(`Erro no servidor ao atualizar filial: ${backendMsg}`);
          } else {
            toastError(backendMsg);
          }
        }
      }

      setSuccessModalData({
        isOpen: true,
        tceId: novoId,
        role: "estagiario",
      });
    } catch (error) {
      console.error("ERRO AO SALVAR TCA:", error);

      if (error.response?.status === 400) {
        const data = error.response.data;
        const { anexosObrigatorios, camposFaltando } =
          parseAnexosObrigatoriosFromError(data);

        if (anexosObrigatorios.length > 0) {
          const idsDestacar = anexosObrigatorios
            .map((a) => a.id)
            .filter(Boolean);
          setAnexosObrigatoriosDestacar(idsDestacar);
        } else {
          setAnexosObrigatoriosDestacar([]);
        }

        if (anexosObrigatorios.length > 0 || camposFaltando.length > 0) {
          const items = [];
          anexosObrigatorios.forEach((a) => {
            const normalizedId = normalizeMissingItemId(a.id);
            items.push({ id: normalizedId, label: a.label || normalizedId });
          });
          camposFaltando.forEach((c) => {
            let customLabel = c;
            let customId = c;
            if (c === "tutor_aprendiz_empresa") {
              customLabel = "Nome do responsável para receber avaliações";
              customId = "nome_tutor";
            }
            const normalizedId = normalizeMissingItemId(customId);
            items.push({
              id: normalizedId,
              label: customLabel || normalizedId,
            });
          });
          if (items.length > 0) {
            setMissingFieldsAlert(dedupeMissingItems(items));
            submittingRef.current = false;
            return;
          }
        }
      }

      if (error.response?.data?.errors) {
        toastError(`Erro: ${error.response.data.errors.join(" | ")}`);
      } else {
        toastError("Erro ao salvar TCA");
      }
    } finally {
      submittingRef.current = false;
    }
  };

  const aprovar = async () => {
    const ok = await confirm({
      title: "Aprovar TCA",
      message:
        "Tem certeza que deseja APROVAR este TCA?\nEsta ação não pode ser desfeita.",
      confirmText: "Sim, aprovar",
      cancelText: "Cancelar",
      variant: "success",
    });
    if (!ok) return;

    try {
      const id = formData.id;
      const token = localStorage.getItem("token");
      await axios.put(
        `${API_BASE_URL}/tca/${id}/aprovar`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toastSuccess("TCA aprovado!");
    } catch (error) {
      console.error(error);
      toastError(
        error.response?.data?.errors?.[0] ||
          error.response?.data?.erro ||
          "Erro ao aprovar",
      );
    }
  };

  const recusar = async () => {
    const motivo = await promptUser({
      title: "Recusar TCA",
      message: "Informe o motivo da recusa:",
      inputPlaceholder: "Ex: documentos incompletos...",
      confirmText: "Recusar TCA",
      cancelText: "Cancelar",
      variant: "danger",
    });
    if (!motivo) return;

    try {
      const id = formData.id;
      const token = localStorage.getItem("token");
      await axios.put(
        `${API_BASE_URL}/tca/${id}/recusar`,
        { motivo },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toastWarning("TCA recusado!");
    } catch (error) {
      console.error(error);
      toastError(
        error.response?.data?.errors?.[0] ||
          error.response?.data?.erro ||
          "Erro ao recusar",
      );
    }
  };

  const handleVoltarInicio = () => {
    try {
      window.localStorage.clear();
      window.sessionStorage.clear();
    } catch (_) {}
    window.location.href = "/";
  };

  const verificarPermissaoTCA = () => {
    const token = localStorage.getItem("token");
    if (!token) return false;
    try {
      const decoded = jwtDecode(token);
      const tipo = decoded.tipo;
      const tipoPrograma = Number(decoded.tipo_programa);
      if (tipo === "usuario_ciee") return true;
      if (tipoPrograma === 1 || tipoPrograma === 2) return true;
      return false;
    } catch (e) {
      return false;
    }
  };

  if (!verificarPermissaoTCA()) {
    return <Navigate to="/dashboard" replace />;
  }
  if (!autorizado) return null;
  if (Number(user?.acesso_autorizacao ?? 0) === 1 && !formData.id) {
    return (
      <div className="tca-container tca-py-5 tca-text-center">
        <h2 className="tca-h2">Aguardando Empresa</h2>
        <p
          className="tca-text-muted"
          style={{ maxWidth: "500px", margin: "1rem auto" }}
        >
          Você ainda não tem um TCA para preencher.
          <br />
          <br />A empresa precisa criar o TCA primeiro. Depois você poderá abrir
          aqui e preencher seus dados.
        </p>
        <button
          type="button"
          className="tca-btn tca-btn-secondary"
          onClick={handleVoltarInicio}
          style={{ marginTop: "1rem" }}
        >
          Voltar ao Início
        </button>
      </div>
    );
  }

  const municipioAtualTca = formData.municipio;

  const normalizarTexto = (v) =>
    String(v || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toUpperCase();

  const municipioBate = (h, municipioAlvo) => {
    if (!municipioAlvo) return false;
    const alvoNorm = normalizarTexto(municipioAlvo);
    const candidatos = [
      h?.municipios,
      h?.municipio,
      h?.cidade,
      h?.cidades,
      h?.municipios_nomes,
    ];
    for (const c of candidatos) {
      if (!c) continue;
      const lista = Array.isArray(c) ? c : [c];
      for (const item of lista) {
        if (item == null) continue;
        if (typeof item === "object") {
          const possiveis = [
            item.nome,
            item.municipio,
            item.cidade,
            item.descricao,
            item.id,
            item.municipio_id,
          ];
          for (const p of possiveis) {
            if (p != null && normalizarTexto(p) === alvoNorm) return true;
          }
        } else if (normalizarTexto(item) === alvoNorm) {
          return true;
        }
      }
    }
    return false;
  };

  const horariosTeoricosDisponiveis = horariosTeoricos.filter((h) => {
    if (!municipioAtualTca) return true;
    return municipioBate(h, municipioAtualTca);
  });

  return (
    <>
      <form
        className="form-tca form-tca-fade tca-container"
        onSubmit={handleSubmit}
      >
        <h2 className="tca-h2">TCA — Programa Aprendiz</h2>

        {/* === SEÇÃO 1: Dados da Empresa === */}
        <div className="div-dados-empresa-title">
          <label htmlFor="dados-empresa-title">
            Dados da Empresa Contratante
          </label>
        </div>

        <div className="div-tca-one">
          <div className="div-razao-social">
            <label className="all-label" htmlFor="razao-social">
              Razão Social:
            </label>
            <input
              type="text"
              name="razao_social"
              className="razao-social"
              id="razao_social"
              disabled={isEmpresaReadOnly}
              readOnly={isEmpresaReadOnly}
              maxLength={200}
              onChange={handleChange}
              value={formData.razao_social}
            />
          </div>
        </div>

        <div className="div-tca-two">
          <div className="div-cnpj-cpf">
            <label className="all-label" htmlFor="cnpj_ou_cpf">
              CNPJ/CPF:
            </label>
            <input
              type="text"
              name="cnpj_ou_cpf"
              className="cnpj-cpf"
              id="cnpj_ou_cpf"
              disabled={isEmpresaReadOnly}
              readOnly={isEmpresaReadOnly}
              onChange={handleChange}
              maxLength={18}
              value={formatarCnpjCpf(formData.cnpj_ou_cpf)}
            />
          </div>

          <div className="div-tca-telefone">
            <label className="all-label" htmlFor="telefone_empresa_primario">
              Telefone:
            </label>
            <InputMask
              mask="(99)99999-9999"
              maskChar=" "
              type="text"
              name="telefone_empresa_primario"
              autoComplete="off"
              className="tca-telefone"
              id="telefone_empresa_primario"
              disabled={isEmpresaReadOnly}
              readOnly={isEmpresaReadOnly}
              value={formData.telefone_empresa_primario}
              onChange={handleChange}
            />
          </div>

          <div className="div-tca-email">
            <label className="all-label" htmlFor="email_empresa">
              E-mail:
            </label>
            <input
              type="email"
              name="email_empresa"
              className="tca-email"
              id="email_empresa"
              disabled={isEmpresaReadOnly}
              readOnly={isEmpresaReadOnly}
              maxLength={80}
              onChange={handleChange}
              value={formData.email_empresa}
              style={
                emailErrors.email_empresa
                  ? {
                      borderColor: "#ff6b6b",
                      boxShadow: "0 0 0 2px rgba(255,107,107,0.25)",
                    }
                  : undefined
              }
            />
            {emailErrors.email_empresa && (
              <span
                style={{ color: "#ff6b6b", fontSize: "0.8rem", marginTop: 4 }}
              >
                E-mail inválido.
              </span>
            )}
          </div>
        </div>

        {mostrarFiliais && (
          <div
            className="div-tca-one"
            style={{ marginTop: "10px", marginBottom: "20px" }}
          >
            <div style={{ width: "100%" }}>
              <label className="all-label" htmlFor="unidade_select">
                Unidade:
              </label>
              <select
                id="unidade_select"
                style={{
                  width: "100%",
                  padding: "5px",
                  borderRadius: "4px",
                  border: "1px solid #7a7a7a",
                  backgroundColor: "#fff",
                  height: "35px",
                  outline: "none",
                }}
                value={
                  localAtividade === "MATRIZ"
                    ? "MATRIZ"
                    : localAtividade &&
                        !isNaN(Number(localAtividade)) &&
                        Number(localAtividade) !== 0
                      ? "FILIAL"
                      : localAtividade === "FILIAL"
                        ? "FILIAL"
                        : ""
                }
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "MATRIZ") {
                    setLocalAtividade("MATRIZ");
                    setFormData((prev) => ({
                      ...prev,
                      local_realizacao_atividades: "MATRIZ",
                    }));
                  } else if (val === "FILIAL") {
                    // Mantém o ID da filial já selecionada; se não houver
                    // nenhuma, usa o sentinela "FILIAL" para forçar
                    // escolha no campo "Selecione a filial".
                    if (
                      localAtividade &&
                      localAtividade !== "MATRIZ" &&
                      localAtividade !== "FILIAL" &&
                      !isNaN(Number(localAtividade))
                    ) {
                      setFormData((prev) => ({
                        ...prev,
                        local_realizacao_atividades: localAtividade,
                      }));
                    } else {
                      setLocalAtividade("FILIAL");
                      setFormData((prev) => ({
                        ...prev,
                        local_realizacao_atividades: "",
                      }));
                    }
                  } else {
                    setLocalAtividade("");
                    setFormData((prev) => ({
                      ...prev,
                      local_realizacao_atividades: "",
                    }));
                  }
                }}
              >
                <option value="">Selecione</option>
                <option value="MATRIZ">Matriz</option>
                <option value="FILIAL">Filial</option>
              </select>
              {filialLoading && (
                <span
                  style={{
                    color: "#3b82f6",
                    fontSize: "0.85rem",
                    marginTop: "4px",
                    display: "inline-block",
                  }}
                >
                  Carregando dados da unidade...
                </span>
              )}
            </div>
          </div>
        )}

        {mostrarFiliais &&
          localAtividade &&
          String(localAtividade) !== "MATRIZ" && (
            <>
              <div className="tca-card" style={{ marginTop: "15px" }}>
                <div className="div-dados-empresa-title tca-card-header">
                  <label>Informações da Filial</label>
                </div>
                <div className="tca-card-body">
                  <div className="tca-row">
                    <div
                      className="tca-col-md-12 tca-mb-3"
                      id="tca-section-filial"
                    >
                      <label>
                        Selecione a Filial Específica (Auto-preenchimento)
                      </label>
                      <select
                        className="tca-form-control"
                        value={localAtividade}
                        onChange={(e) => {
                          const val = e.target.value;
                          setLocalAtividade(val);
                          setFormData((prev) => ({
                            ...prev,
                            local_realizacao_atividades: val,
                          }));
                        }}
                      >
                        <option value="">Selecione a filial</option>
                        {filiais
                          .filter(
                            (f) =>
                              f.id !== 0 && f.id !== undefined && f.id !== null,
                          )
                          .map((f) => (
                            <option key={f.id} value={f.id}>
                              {f.nome_filial ||
                                f.razao_social ||
                                f.nome ||
                                `Filial (ID: ${f.id})`}
                            </option>
                          ))}
                      </select>
                    </div>

                    <div className="tca-col-md-4 tca-mb-3">
                      <label>Nome da Filial</label>
                      <input
                        className="tca-form-control"
                        name="nome_filial"
                        value={formData.nome_filial || ""}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="tca-col-md-4 tca-mb-3">
                      <label>CPF/CNPJ</label>
                      <input
                        type="text"
                        className="tca-form-control"
                        name="cpfCnpj"
                        value={formData.cpfCnpj || ""}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="tca-col-md-4 tca-mb-3">
                      <label>CEP</label>
                      <input
                        type="text"
                        className="tca-form-control"
                        name="cepFilial"
                        value={formData.cepFilial || ""}
                        onChange={handleChange}
                        maxLength={9}
                        placeholder="Apenas números"
                      />
                    </div>

                    <div className="tca-col-md-4 tca-mb-3">
                      <label>Endereço</label>
                      <input
                        type="text"
                        className="tca-form-control"
                        name="enderecoFilial"
                        value={formData.enderecoFilial || ""}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="tca-col-md-4 tca-mb-3">
                      <label>Bairro/Distrito</label>
                      <input
                        type="text"
                        className="tca-form-control"
                        name="bairro"
                        value={formData.bairro || ""}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="tca-col-md-4 tca-mb-3">
                      <label>Municipio</label>
                      <input
                        type="text"
                        className="tca-form-control"
                        name="municipio"
                        value={formData.municipio || ""}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="tca-col-md-4 tca-mb-3">
                      <label>UF</label>
                      <input
                        type="text"
                        className="tca-form-control"
                        name="uf"
                        value={formData.uf || ""}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="tca-col-md-4 tca-mb-3">
                      <label>Telefone 1</label>
                      <InputMask
                        mask="(99)99999-9999"
                        maskChar=" "
                        className="tca-form-control"
                        name="telefones"
                        value={formData.telefones || ""}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="tca-col-md-4 tca-mb-3">
                      <label>Telefone 2</label>
                      <InputMask
                        mask="(99)99999-9999"
                        maskChar=" "
                        className="tca-form-control"
                        name="telefones2"
                        value={formData.telefones2 || ""}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="tca-col-md-4 tca-mb-3">
                      <label>E-mail Contrato</label>
                      <input
                        type="email"
                        className="tca-form-control"
                        name="emailContratos"
                        value={formData.emailContratos || ""}
                        onChange={handleChange}
                        style={
                          emailErrors.emailContratos
                            ? {
                                borderColor: "#ff6b6b",
                                boxShadow: "0 0 0 2px rgba(255,107,107,0.25)",
                              }
                            : undefined
                        }
                      />
                      {emailErrors.emailContratos && (
                        <span
                          style={{
                            color: "#ff6b6b",
                            fontSize: "0.8rem",
                            marginTop: 4,
                          }}
                        >
                          E-mail inválido.
                        </span>
                      )}
                    </div>

                    <div className="tca-col-md-4 tca-mb-3">
                      <label>E-mail Financeiro</label>
                      <input
                        type="email"
                        className="tca-form-control"
                        name="emailFinanceiro"
                        value={formData.emailFinanceiro || ""}
                        onChange={handleChange}
                        style={
                          emailErrors.emailFinanceiro
                            ? {
                                borderColor: "#ff6b6b",
                                boxShadow: "0 0 0 2px rgba(255,107,107,0.25)",
                              }
                            : undefined
                        }
                      />
                      {emailErrors.emailFinanceiro && (
                        <span
                          style={{
                            color: "#ff6b6b",
                            fontSize: "0.8rem",
                            marginTop: 4,
                          }}
                        >
                          E-mail inválido.
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="tca-card" style={{ marginTop: "15px" }}>
                <div className="div-dados-empresa-title tca-card-header">
                  <label>Representante da Filial</label>
                </div>

                <div className="tca-card-body">
                  <div className="tca-row">
                    <div className="tca-col-md-4 tca-mb-3">
                      <label>Representante Por</label>
                      <input
                        type="text"
                        className="tca-form-control"
                        name="representadaPor"
                        value={formData.representadaPor || ""}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="tca-col-md-4 tca-mb-3">
                      <label>Cargo</label>
                      <input
                        type="text"
                        className="tca-form-control"
                        name="cargoRepresentante"
                        value={formData.cargoRepresentante || ""}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="tca-col-md-4 tca-mb-3">
                      <label>E-mail</label>
                      <input
                        type="email"
                        className="tca-form-control"
                        name="emailRepresentante"
                        value={formData.emailRepresentante || ""}
                        onChange={handleChange}
                        style={
                          emailErrors.emailRepresentante
                            ? {
                                borderColor: "#ff6b6b",
                                boxShadow: "0 0 0 2px rgba(255,107,107,0.25)",
                              }
                            : undefined
                        }
                      />
                      {emailErrors.emailRepresentante && (
                        <span
                          style={{
                            color: "#ff6b6b",
                            fontSize: "0.8rem",
                            marginTop: 4,
                          }}
                        >
                          E-mail inválido.
                        </span>
                      )}
                    </div>

                    <div className="tca-col-md-4 tca-mb-3">
                      <label>Responsável/Supervisor do Estágio</label>
                      <input
                        type="text"
                        className="tca-form-control"
                        name="responsavelSupervisor"
                        value={formData.responsavelSupervisor || ""}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="tca-col-md-4 tca-mb-3">
                      <label>Cargo</label>
                      <input
                        type="text"
                        className="tca-form-control"
                        name="cargoSupervisor"
                        value={formData.cargoSupervisor || ""}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="tca-col-md-4 tca-mb-3">
                      <label>E-mail</label>
                      <input
                        type="email"
                        className="tca-form-control"
                        name="emailSupervisor"
                        value={formData.emailSupervisor || ""}
                        onChange={handleChange}
                        style={
                          emailErrors.emailSupervisor
                            ? {
                                borderColor: "#ff6b6b",
                                boxShadow: "0 0 0 2px rgba(255,107,107,0.25)",
                              }
                            : undefined
                        }
                      />
                      {emailErrors.emailSupervisor && (
                        <span
                          style={{
                            color: "#ff6b6b",
                            fontSize: "0.8rem",
                            marginTop: 4,
                          }}
                        >
                          E-mail inválido.
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        {/* === SEÇÃO 2: Dados do Representante === */}
        <div className="div-representante-legal-title">
          <label htmlFor="representante-legal-title">
            Representante Legal (Quem irá assinar o contrato)
          </label>
        </div>

        <div className="div-tca-three">
          <div className="div-tca-nome">
            <label className="all-label" htmlFor="nome_empresa">
              Nome:
            </label>
            <input
              type="text"
              name="nome_empresa"
              className="tca-nome"
              id="nome_empresa"
              disabled={!permissao.podeEditarEmpresa}
              maxLength={100}
              onChange={handleChange}
              value={formData.nome_empresa}
            />
          </div>
          <div className="div-tca-row">
            <div className="div-tca-cargo">
              <label className="all-label" htmlFor="cargo_empresa">
                Cargo:
              </label>
              <input
                type="text"
                name="cargo_empresa"
                className="tca-cargo"
                id="cargo_empresa"
                disabled={!permissao.podeEditarEmpresa}
                maxLength={100}
                onChange={handleChange}
                value={formData.cargo_empresa}
              />
            </div>
            <div className="div-tca-telefone">
              <label className="all-label" htmlFor="telefone_empresa">
                Telefone:
              </label>
              <InputMask
                mask="(99)99999-9999"
                maskChar=" "
                type="text"
                name="telefone_empresa"
                disabled={!permissao.podeEditarEmpresa}
                autoComplete="off"
                className="tca-telefone"
                id="telefone_empresa"
                onChange={handleChange}
                value={formData.telefone_empresa || ""}
              />
            </div>
            <div className="div-tca-email">
              <label className="all-label" htmlFor="email_empresa_contratante">
                Email:
              </label>
              <input
                type="email"
                name="email_empresa_contratante"
                className="tca-email"
                id="email_empresa_contratante"
                disabled={!permissao.podeEditarEmpresa}
                maxLength={100}
                onChange={handleChange}
                value={formData.email_empresa_contratante}
                style={
                  emailErrors.email_empresa_contratante
                    ? {
                        borderColor: "#ff6b6b",
                        boxShadow: "0 0 0 2px rgba(255,107,107,0.25)",
                      }
                    : undefined
                }
              />
              {emailErrors.email_empresa_contratante && (
                <span
                  style={{ color: "#ff6b6b", fontSize: "0.8rem", marginTop: 4 }}
                >
                  E-mail inválido.
                </span>
              )}
            </div>
          </div>
        </div>

        <div
          className="div-local-execucao-pergunta"
          style={{ marginBottom: "1rem" }}
        >
          <label style={{ fontWeight: 500, marginRight: "1rem" }}>
            O local de execução das atividades práticas é diferente do CNPJ
            informado acima?
          </label>

          <div
            className="tca-radio-group"
            style={{ display: "inline-block", marginLeft: "1rem" }}
          >
            <label
              className="tca-form-check tca-form-check-inline"
              style={{ marginRight: "1rem", cursor: "pointer" }}
            >
              <input
                type="radio"
                name="local_execucao_diferente"
                value="1"
                checked={
                  formData.local_execucao_diferente === true ||
                  formData.local_execucao_diferente === 1
                }
                onChange={() =>
                  setFormData((prev) => ({
                    ...prev,
                    local_execucao_diferente: true,
                  }))
                }
                style={{ marginRight: "0.4rem" }}
              />
              <span>Sim</span>
            </label>

            <label
              className="tca-form-check tca-form-check-inline"
              style={{ cursor: "pointer" }}
            >
              <input
                type="radio"
                name="local_execucao_diferente"
                value="0"
                checked={
                  formData.local_execucao_diferente === false ||
                  formData.local_execucao_diferente === 0
                }
                onChange={() =>
                  setFormData((prev) => ({
                    ...prev,
                    local_execucao_diferente: false,
                    emp_local_razao_social: "",
                    emp_local_cnpj: "",
                    emp_local_telefone: "",
                    emp_local_email: "",
                    emp_local_endereco: "",
                    emp_local_bairro: "",
                    emp_local_cidade: "",
                    emp_local_uf: "",
                    emp_local_cep: "",
                    emp_local_rep_nome: "",
                    emp_local_rep_cargo: "",
                    emp_local_rep_telefone: "",
                    emp_local_rep_email: "",
                    resp_admin_nome: "",
                    resp_admin_cargo: "",
                    resp_admin_telefone: "",
                    resp_admin_email: "",
                  }))
                }
                style={{ marginRight: "0.4rem" }}
              />
              <span>Não</span>
            </label>
          </div>
        </div>

        {Boolean(formData.local_execucao_diferente) && (
          <div
            className="tca-col-md-12"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "1.5rem",
              padding: "0 15px",
              marginBottom: "2rem",
            }}
          >
            {/* Card 1: Dados da Empresa */}
            <div
              style={{
                background: "rgba(255, 255, 255, 0.04)",
                border: "1px solid rgba(255, 255, 255, 0.15)",
                borderRadius: "12px",
                padding: "1.5rem",
              }}
            >
              <div
                style={{
                  marginBottom: "1.5rem",
                  borderBottom: "1px solid rgba(255,255,255,0.1)",
                  paddingBottom: "0.5rem",
                }}
              >
                <h4
                  style={{
                    color: "#fff",
                    fontSize: "1.1rem",
                    fontWeight: "bold",
                    margin: 0,
                  }}
                >
                  1. Dados da Empresa Contratante (Local de Execução das
                  Atividades Práticas)
                </h4>
              </div>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  margin: "0 -0.5rem",
                }}
              >
                <div
                  className="tca-col-md-12"
                  style={{ padding: "0 0.5rem", marginBottom: "1rem" }}
                >
                  <label
                    style={{
                      color: "#fff",
                      marginBottom: "0.25rem",
                      display: "block",
                    }}
                  >
                    Razão Social:
                  </label>
                  <input
                    type="text"
                    className="tca-form-control"
                    name="emp_local_razao_social"
                    disabled={!permissao.podeEditarEmpresa}
                    maxLength={200}
                    onChange={handleChange}
                    value={formData.emp_local_razao_social || ""}
                    style={{ borderRadius: "20px" }}
                  />
                </div>
                <div
                  className="tca-col-md-4"
                  style={{ padding: "0 0.5rem", marginBottom: "1rem" }}
                >
                  <label
                    style={{
                      color: "#fff",
                      marginBottom: "0.25rem",
                      display: "block",
                    }}
                  >
                    CNPJ:
                  </label>
                  <input
                    type="text"
                    className="tca-form-control"
                    name="emp_local_cnpj"
                    disabled={!permissao.podeEditarEmpresa}
                    maxLength={18}
                    onChange={handleChange}
                    value={formatarCnpjCpf(formData.emp_local_cnpj) || ""}
                    style={{ borderRadius: "20px" }}
                  />
                </div>
                <div
                  className="tca-col-md-4"
                  style={{ padding: "0 0.5rem", marginBottom: "1rem" }}
                >
                  <label
                    style={{
                      color: "#fff",
                      marginBottom: "0.25rem",
                      display: "block",
                    }}
                  >
                    Telefone:
                  </label>
                  <InputMask
                    mask="(99)99999-9999"
                    maskChar=" "
                    type="text"
                    className="tca-form-control"
                    name="emp_local_telefone"
                    autoComplete="off"
                    disabled={!permissao.podeEditarEmpresa}
                    onChange={handleChange}
                    value={formData.emp_local_telefone || ""}
                    style={{ borderRadius: "20px" }}
                  />
                </div>
                <div
                  className="tca-col-md-4"
                  style={{ padding: "0 0.5rem", marginBottom: "1rem" }}
                >
                  <label
                    style={{
                      color: "#fff",
                      marginBottom: "0.25rem",
                      display: "block",
                    }}
                  >
                    E-mail:
                  </label>
                  <input
                    type="email"
                    className="tca-form-control"
                    name="emp_local_email"
                    disabled={!permissao.podeEditarEmpresa}
                    maxLength={80}
                    onChange={handleChange}
                    value={formData.emp_local_email || ""}
                    style={{
                      borderRadius: "20px",
                      ...(emailErrors.emp_local_email
                        ? {
                            borderColor: "#ff6b6b",
                            boxShadow: "0 0 0 2px rgba(255,107,107,0.25)",
                          }
                        : {}),
                    }}
                  />
                  {emailErrors.emp_local_email && (
                    <span
                      style={{
                        color: "#ff6b6b",
                        fontSize: "0.8rem",
                        marginTop: 4,
                      }}
                    >
                      E-mail inválido.
                    </span>
                  )}
                </div>
                <div
                  className="tca-col-md-8"
                  style={{ padding: "0 0.5rem", marginBottom: "1rem" }}
                >
                  <label
                    style={{
                      color: "#fff",
                      marginBottom: "0.25rem",
                      display: "block",
                    }}
                  >
                    Endereço:
                  </label>
                  <input
                    type="text"
                    className="tca-form-control"
                    name="emp_local_endereco"
                    disabled={!permissao.podeEditarEmpresa}
                    maxLength={200}
                    onChange={handleChange}
                    value={formData.emp_local_endereco || ""}
                    style={{ borderRadius: "20px" }}
                  />
                </div>
                <div
                  className="tca-col-md-4"
                  style={{ padding: "0 0.5rem", marginBottom: "1rem" }}
                >
                  <label
                    style={{
                      color: "#fff",
                      marginBottom: "0.25rem",
                      display: "block",
                    }}
                  >
                    Bairro:
                  </label>
                  <input
                    type="text"
                    className="tca-form-control"
                    name="emp_local_bairro"
                    disabled={!permissao.podeEditarEmpresa}
                    maxLength={100}
                    onChange={handleChange}
                    value={formData.emp_local_bairro || ""}
                    style={{ borderRadius: "20px" }}
                  />
                </div>
                <div
                  className="tca-col-md-6"
                  style={{ padding: "0 0.5rem", marginBottom: "1rem" }}
                >
                  <label
                    style={{
                      color: "#fff",
                      marginBottom: "0.25rem",
                      display: "block",
                    }}
                  >
                    Cidade:
                  </label>
                  <input
                    type="text"
                    className="tca-form-control"
                    name="emp_local_cidade"
                    disabled={!permissao.podeEditarEmpresa}
                    maxLength={100}
                    onChange={handleChange}
                    value={formData.emp_local_cidade || ""}
                    style={{ borderRadius: "20px" }}
                  />
                </div>
                <div
                  className="tca-col-md-2"
                  style={{ padding: "0 0.5rem", marginBottom: "1rem" }}
                >
                  <label
                    style={{
                      color: "#fff",
                      marginBottom: "0.25rem",
                      display: "block",
                    }}
                  >
                    UF:
                  </label>
                  <input
                    type="text"
                    className="tca-form-control"
                    name="emp_local_uf"
                    disabled={!permissao.podeEditarEmpresa}
                    maxLength={2}
                    onChange={handleChange}
                    value={formData.emp_local_uf || ""}
                    style={{ borderRadius: "20px" }}
                  />
                </div>
                <div
                  className="tca-col-md-4"
                  style={{ padding: "0 0.5rem", marginBottom: "1rem" }}
                >
                  <label
                    style={{
                      color: "#fff",
                      marginBottom: "0.25rem",
                      display: "block",
                    }}
                  >
                    CEP:
                  </label>
                  <InputMask
                    mask="99999-999"
                    maskChar=" "
                    type="text"
                    className="tca-form-control"
                    name="emp_local_cep"
                    autoComplete="off"
                    disabled={!permissao.podeEditarEmpresa}
                    onChange={handleChange}
                    value={formData.emp_local_cep || ""}
                    style={{ borderRadius: "20px" }}
                  />
                </div>
              </div>
            </div>

            {/* Card 2: Representante Legal */}
            <div
              style={{
                background: "rgba(255, 255, 255, 0.04)",
                border: "1px solid rgba(255, 255, 255, 0.15)",
                borderRadius: "12px",
                padding: "1.5rem",
              }}
            >
              <div
                style={{
                  marginBottom: "1.5rem",
                  borderBottom: "1px solid rgba(255,255,255,0.1)",
                  paddingBottom: "0.5rem",
                }}
              >
                <h4
                  style={{
                    color: "#fff",
                    fontSize: "1.05rem",
                    fontWeight: "bold",
                    margin: 0,
                  }}
                >
                  2.Representante Legal
                </h4>
              </div>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  margin: "0 -0.5rem",
                }}
              >
                <div
                  className="tca-col-md-6"
                  style={{ padding: "0 0.5rem", marginBottom: "1rem" }}
                >
                  <label
                    style={{
                      color: "#fff",
                      marginBottom: "0.25rem",
                      display: "block",
                    }}
                  >
                    Nome:
                  </label>
                  <input
                    type="text"
                    className="tca-form-control"
                    name="emp_local_rep_nome"
                    disabled={!permissao.podeEditarEmpresa}
                    maxLength={100}
                    onChange={handleChange}
                    value={formData.emp_local_rep_nome || ""}
                    style={{ borderRadius: "20px" }}
                  />
                </div>
                <div
                  className="tca-col-md-6"
                  style={{ padding: "0 0.5rem", marginBottom: "1rem" }}
                >
                  <label
                    style={{
                      color: "#fff",
                      marginBottom: "0.25rem",
                      display: "block",
                    }}
                  >
                    Cargo:
                  </label>
                  <input
                    type="text"
                    className="tca-form-control"
                    name="emp_local_rep_cargo"
                    disabled={!permissao.podeEditarEmpresa}
                    maxLength={100}
                    onChange={handleChange}
                    value={formData.emp_local_rep_cargo || ""}
                    style={{ borderRadius: "20px" }}
                  />
                </div>
                <div
                  className="tca-col-md-6"
                  style={{ padding: "0 0.5rem", marginBottom: "1rem" }}
                >
                  <label
                    style={{
                      color: "#fff",
                      marginBottom: "0.25rem",
                      display: "block",
                    }}
                  >
                    Telefone:
                  </label>
                  <InputMask
                    mask="(99)99999-9999"
                    maskChar=" "
                    type="text"
                    className="tca-form-control"
                    name="emp_local_rep_telefone"
                    autoComplete="off"
                    disabled={!permissao.podeEditarEmpresa}
                    onChange={handleChange}
                    value={formData.emp_local_rep_telefone || ""}
                    style={{ borderRadius: "20px" }}
                  />
                </div>
                <div
                  className="tca-col-md-6"
                  style={{ padding: "0 0.5rem", marginBottom: "1rem" }}
                >
                  <label
                    style={{
                      color: "#fff",
                      marginBottom: "0.25rem",
                      display: "block",
                    }}
                  >
                    E-mail:
                  </label>
                  <input
                    type="email"
                    className="tca-form-control"
                    name="emp_local_rep_email"
                    disabled={!permissao.podeEditarEmpresa}
                    maxLength={100}
                    onChange={handleChange}
                    value={formData.emp_local_rep_email || ""}
                    style={{
                      borderRadius: "20px",
                      ...(emailErrors.emp_local_rep_email
                        ? {
                            borderColor: "#ff6b6b",
                            boxShadow: "0 0 0 2px rgba(255,107,107,0.25)",
                          }
                        : {}),
                    }}
                  />
                  {emailErrors.emp_local_rep_email && (
                    <span
                      style={{
                        color: "#ff6b6b",
                        fontSize: "0.8rem",
                        marginTop: 4,
                      }}
                    >
                      E-mail inválido.
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Card 3: Responsável Administrativo */}
            <div
              style={{
                background: "rgba(255, 255, 255, 0.04)",
                border: "1px solid rgba(255, 255, 255, 0.15)",
                borderRadius: "12px",
                padding: "1.5rem",
              }}
            >
              <div
                style={{
                  marginBottom: "1.5rem",
                  borderBottom: "1px solid rgba(255,255,255,0.1)",
                  paddingBottom: "0.5rem",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <h4
                  style={{
                    color: "#fff",
                    fontSize: "1.05rem",
                    fontWeight: "bold",
                    margin: 0,
                  }}
                >
                  3. Responsável Administrativo
                </h4>
              </div>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  margin: "0 -0.5rem",
                }}
              >
                <div
                  className="tca-col-md-6"
                  style={{ padding: "0 0.5rem", marginBottom: "1rem" }}
                >
                  <label
                    style={{
                      color: "#fff",
                      marginBottom: "0.25rem",
                      display: "block",
                    }}
                  >
                    Nome:
                  </label>
                  <input
                    type="text"
                    className="tca-form-control"
                    name="resp_admin_nome"
                    disabled={!permissao.podeEditarEmpresa}
                    maxLength={100}
                    onChange={handleChange}
                    value={formData.resp_admin_nome || ""}
                    style={{ borderRadius: "20px" }}
                  />
                </div>
                <div
                  className="tca-col-md-6"
                  style={{ padding: "0 0.5rem", marginBottom: "1rem" }}
                >
                  <label
                    style={{
                      color: "#fff",
                      marginBottom: "0.25rem",
                      display: "block",
                    }}
                  >
                    Cargo:
                  </label>
                  <input
                    type="text"
                    className="tca-form-control"
                    name="resp_admin_cargo"
                    disabled={!permissao.podeEditarEmpresa}
                    maxLength={100}
                    onChange={handleChange}
                    value={formData.resp_admin_cargo || ""}
                    style={{ borderRadius: "20px" }}
                  />
                </div>
                <div
                  className="tca-col-md-6"
                  style={{ padding: "0 0.5rem", marginBottom: "1rem" }}
                >
                  <label
                    style={{
                      color: "#fff",
                      marginBottom: "0.25rem",
                      display: "block",
                    }}
                  >
                    Telefone:
                  </label>
                  <InputMask
                    mask="(99)99999-9999"
                    maskChar=" "
                    type="text"
                    className="tca-form-control"
                    name="resp_admin_telefone"
                    autoComplete="off"
                    disabled={!permissao.podeEditarEmpresa}
                    onChange={handleChange}
                    value={formData.resp_admin_telefone || ""}
                    style={{ borderRadius: "20px" }}
                  />
                </div>
                <div
                  className="tca-col-md-6"
                  style={{ padding: "0 0.5rem", marginBottom: "1rem" }}
                >
                  <label
                    style={{
                      color: "#fff",
                      marginBottom: "0.25rem",
                      display: "block",
                    }}
                  >
                    E-mail:
                  </label>
                  <input
                    type="email"
                    className="tca-form-control"
                    name="resp_admin_email"
                    disabled={!permissao.podeEditarEmpresa}
                    maxLength={100}
                    onChange={handleChange}
                    value={formData.resp_admin_email || ""}
                    style={{
                      borderRadius: "20px",
                      ...(emailErrors.resp_admin_email
                        ? {
                            borderColor: "#ff6b6b",
                            boxShadow: "0 0 0 2px rgba(255,107,107,0.25)",
                          }
                        : {}),
                    }}
                  />
                  {emailErrors.resp_admin_email && (
                    <span
                      style={{
                        color: "#ff6b6b",
                        fontSize: "0.8rem",
                        marginTop: 4,
                      }}
                    >
                      E-mail inválido.
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div
          className="div-dados-aprendizagem-title"
          style={{ position: "relative" }}
        >
          <label htmlFor="dados-aprendizagem-title">Dados do(a) Aprendiz</label>
          {idadeCalculada !== null && (
            <span
              style={{
                position: "absolute",
                right: "16px",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "0.80rem",
                padding: "4px 10px",
                borderRadius: "12px",
                backgroundColor: idadeCalculada >= 18 ? "#dcfce7" : "#fef08a",
                color: idadeCalculada >= 18 ? "#166534" : "#854d0e",
                fontWeight: "700",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
              }}
            >
              {idadeCalculada >= 18 ? "Maior de Idade" : "Menor de Idade"}
            </span>
          )}
        </div>

        <div className="div-tca-five">
          {estudanteNaoEncontrado && (
            <div
              style={{
                backgroundColor: "#fef2f2",
                border: "1px solid #f87171",
                borderLeft: "4px solid #ef4444",
                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                color: "#991b1b",
                padding: "1rem 1.25rem",
                borderRadius: "8px",
                marginTop: "1.5rem",
                marginBottom: "1.5rem",
                display: "flex",
                alignItems: "flex-start",
                gap: "12px",
                lineHeight: "1.5",
              }}
            >
              <AlertCircle
                size={22}
                style={{ flexShrink: 0, marginTop: "2px", color: "#ef4444" }}
              />
              <div
                style={{ display: "flex", flexDirection: "column", gap: "4px" }}
              >
                <span style={{ fontWeight: "700", fontSize: "0.95rem" }}>
                  Aprendiz não encontrado
                </span>
                <span style={{ fontSize: "0.9rem", color: "#7f1d1d" }}>
                  O contrato não poderá ser validado enquanto o cadastro do(a)
                  aprendiz não for realizado no sistema.
                </span>
              </div>
            </div>
          )}

          <div className="div-tca-five-1">
            <div className="div-tca-cpf_aprendiz">
              <label htmlFor="cpf_aprendiz">CPF do Aprendiz:</label>
              <input
                type="text"
                name="cpf_aprendiz"
                id="cpf_aprendiz"
                maxLength={14}
                onChange={handleChange}
                value={formData.cpf_aprendiz || ""}
              />
            </div>

            <div className="div-tca-five-1-nome">
              <label htmlFor="nome_aprendiz">Nome Completo:</label>
              <input
                type="text"
                name="nome_aprendiz"
                id="nome_aprendiz"
                maxLength={100}
                onChange={handleChange}
                value={formData.nome_aprendiz}
              />
            </div>

            <div className="div-informacoes-contato">
              <label htmlFor="telefone_contato">
                Telefone/Informações adicionais de contato:
              </label>
              <InputMask
                mask="(99)99999-9999"
                maskChar=" "
                type="text"
                name="telefone_contato"
                id="telefone_contato"
                autoComplete="off"
                onChange={handleChange}
                value={formData.telefone_contato || ""}
              />
            </div>

            <div className="div-tca-five-1-email">
              <label htmlFor="email_aprendiz">E-mail:</label>
              <input
                type="email"
                name="email_aprendiz"
                id="email_aprendiz"
                maxLength={100}
                onChange={handleChange}
                value={formData.email_aprendiz}
                style={
                  emailErrors.email_aprendiz
                    ? {
                        borderColor: "#ff6b6b",
                        boxShadow: "0 0 0 2px rgba(255,107,107,0.25)",
                      }
                    : undefined
                }
              />
              {emailErrors.email_aprendiz && (
                <span
                  style={{ color: "#ff6b6b", fontSize: "0.8rem", marginTop: 4 }}
                >
                  E-mail inválido.
                </span>
              )}
            </div>
          </div>

          {estudanteNaoEncontrado && (
            <div
              style={{
                backgroundColor: "#ffffff",
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                padding: "1.25rem 1.5rem",
                marginBottom: "1.5rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "24px",
                boxShadow:
                  "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)",
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                  flex: "1 1 300px",
                }}
              >
                <span
                  style={{
                    fontWeight: "700",
                    color: "#1e293b",
                    fontSize: "0.95rem",
                  }}
                >
                  Precisa de ajuda com o cadastro?
                </span>
                <span
                  style={{
                    fontSize: "0.85rem",
                    color: "#475569",
                    lineHeight: "1.5",
                  }}
                >
                  Em caso de dúvida entre em contato com nossa empresa através.
                  <br />
                  Telefone/Whatsapp: <strong>(31) 3428100</strong> ; E-mail:{" "}
                  <a
                    href="mailto:atendimento@cieemg.org.br"
                    style={{
                      color: "#185fa5",
                      textDecoration: "none",
                      fontWeight: "600",
                    }}
                  >
                    atendimento@cieemg.org.br
                  </a>
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <button
                  type="button"
                  onClick={() => setCienteCadastro(true)}
                  disabled={cienteCadastro}
                  style={{
                    backgroundColor: cienteCadastro ? "#dcfce7" : "#f1f5f9",
                    color: cienteCadastro ? "#166534" : "#475569",
                    border: cienteCadastro
                      ? "1px solid #bbf7d0"
                      : "1px solid #cbd5e1",
                    borderRadius: "8px",
                    padding: "0.75rem 1.25rem",
                    fontWeight: "600",
                    fontSize: "0.95rem",
                    cursor: cienteCadastro ? "default" : "pointer",
                    transition: "all 0.2s ease",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  {cienteCadastro ? (
                    <>
                      <Check size={18} />
                      Ciente
                    </>
                  ) : (
                    "Estou Ciente da Situação"
                  )}
                </button>

                <button
                  type="button"
                  disabled={!cienteCadastro}
                  onClick={() =>
                    window.open("https://appcadastro.cieemg.org.br/", "_blank")
                  }
                  style={{
                    backgroundColor: cienteCadastro ? "#185fa5" : "#94a3b8",
                    color: "#ffffff",
                    border: "none",
                    borderRadius: "8px",
                    padding: "0.75rem 1.75rem",
                    fontWeight: "600",
                    fontSize: "0.95rem",
                    cursor: cienteCadastro ? "pointer" : "not-allowed",
                    boxShadow: cienteCadastro
                      ? "0 4px 6px rgba(24, 95, 165, 0.2)"
                      : "none",
                    transition: "all 0.2s ease",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    opacity: cienteCadastro ? 1 : 0.7,
                  }}
                  onMouseOver={(e) => {
                    if (!cienteCadastro) return;
                    e.target.style.backgroundColor = "#10477e";
                    e.target.style.transform = "translateY(-2px)";
                    e.target.style.boxShadow =
                      "0 6px 8px rgba(24, 95, 165, 0.3)";
                  }}
                  onMouseOut={(e) => {
                    if (!cienteCadastro) return;
                    e.target.style.backgroundColor = "#185fa5";
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow =
                      "0 4px 6px rgba(24, 95, 165, 0.2)";
                  }}
                >
                  Fazer Cadastro do Aprendiz
                </button>
              </div>
            </div>
          )}

          <label htmlFor="div-tca-five-lbl" className="div-tca-five-lbl">
            Observações: <br />
            1)É de suma importância a inscrição do jovem em nosso site com
            antecedência. <br />
            2)Observar se o horário escolar é contrário ao horário de
            aprendizagem. <br />
            3)O início da aprendizagem só poder ocorrer após assinatura do
            contrato.
          </label>
        </div>

        {isMenorDeIdade(idadeCalculada) && (
          <section className="tca-card tca-mb-4 responsavel-aprendiz-section">
            <div className="tca-card-header responsavel-aprendiz-header">
              Responsável Legal do Aprendiz
              <small
                style={{
                  marginLeft: "0.75rem",
                  fontWeight: 500,
                  fontSize: "0.85rem",
                  opacity: 0.85,
                }}
              >
                (Aprendiz menor de idade — {idadeCalculada} anos)
              </small>
            </div>
            <div className="tca-card-body">
              <div className="tca-row">
                <div className="tca-col-md-6 tca-mb-3">
                  <label>Nome</label>
                  <input
                    className="tca-form-control"
                    name="nome_aprendiz_menor_idade"
                    value={formData.nome_aprendiz_menor_idade || ""}
                    onChange={handleChange}
                  />
                </div>
                <div className="tca-col-md-3 tca-mb-3">
                  <label>Telefone</label>
                  <InputMask
                    mask="(99)99999-9999"
                    maskChar=" "
                    className="tca-form-control"
                    name="telefone_aprendiz_menor_idade"
                    value={formData.telefone_aprendiz_menor_idade || ""}
                    onChange={handleChange}
                  />
                </div>
                <div className="tca-col-md-3 tca-mb-3">
                  <label>CPF</label>
                  <InputMask
                    mask="999.999.999-99"
                    maskChar=" "
                    className="tca-form-control"
                    name="cpf_aprendiz_menor_idade"
                    value={formData.cpf_aprendiz_menor_idade || ""}
                    onChange={handleChange}
                  />
                </div>
                <div className="tca-col-md-6 tca-mb-3">
                  <label>E-mail</label>
                  <input
                    type="email"
                    className="tca-form-control"
                    name="email_aprendiz_menor_idade"
                    value={formData.email_aprendiz_menor_idade || ""}
                    onChange={handleChange}
                    style={
                      emailErrors.email_aprendiz_menor_idade
                        ? {
                            borderColor: "#ff6b6b",
                            boxShadow: "0 0 0 2px rgba(255,107,107,0.25)",
                          }
                        : undefined
                    }
                  />
                  {emailErrors.email_aprendiz_menor_idade && (
                    <span
                      style={{
                        color: "#ff6b6b",
                        fontSize: "0.8rem",
                        marginTop: 4,
                      }}
                    >
                      E-mail inválido.
                    </span>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}

        {!isStudent && (
          <div
            style={{
              margin: "0 1rem",
              padding: "10px 14px",
              borderRadius: 8,
              background: "rgba(24, 95, 165, 0.08)",
              border: "1px solid rgba(24, 95, 165, 0.35)",
              color: "#10477e",
              fontSize: 13,
              lineHeight: 1.45,
            }}
          >
            <strong>Anexos do cadastro.</strong> A empresa pode anexar os
            documentos do estudante caso já os tenha em mãos (comprovante de
            endereço, declaração escolar/histórico e documento de
            identificação). O estudante também pode anexar pela área de edição
            do aprendiz.
          </div>
        )}

        <DocumentUploadForm
          schema={ANEXOS_SCHEMA_TCA_COMUM}
          filesById={anexosFiles}
          statusById={anexosStatus}
          highlightIds={anexosObrigatoriosDestacar}
          onAnexar={(id, file) =>
            handleAnexoChange(id)({ target: { files: [file] } })
          }
          disabled={!isStudent}
          topNote={
            isStudent
              ? "Gentileza anexar sua declaração escolar atualizada ou com no máximo 30 dias de emissão. Sem a declaração escolar sua contratação não será efetivada."
              : "Documentos de responsabilidade do estudante — esta seção está bloqueada para o seu perfil porque o envio é feito na área de edição do aprendiz."
          }
          missingMessage={
            isStudent
              ? "Anexe a declaração escolar atualizada, com no máximo 30 dias de emissão. Sem ela, sua contratação não será efetivada."
              : ""
          }
          observations={formData.observacoes2}
          insertObservations={showObs2}
          onInsertObservationsChange={(v) => {
            setShowObs2(v);
            setFormData((p) => ({
              ...p,
              inserir_observacoes_tca_comum: v,
              observacoes2: v ? p.observacoes2 : "",
            }));
          }}
          onObservationsChange={(v) => {
            const regex = /[^0-9a-zA-ZÀ-ÿ\s.,&-]/g;
            const filtered = String(v).replace(regex, "");
            setFormData((p) => ({ ...p, observacoes2: filtered }));
          }}
          showObservationsSection={permissao?.podeVerObservacoesCiee}
        />

        <div className="div-dados-aprendizagem-title">
          <label htmlFor="dados-aprendizagem-title">
            Dados da Aprendizagem
          </label>
        </div>

        <div className="div-tca-four">
          <div className="div-tca-four-2">
            <div className="div-data-inicio-contrato">
              <label className="all-label" htmlFor="data_inicio_contrato">
                Data do início do contrato:
              </label>
              <select
                name="data_inicio_contrato"
                id="data_inicio_contrato"
                disabled={!permissao?.podeEditarEmpresa}
                value={formData.data_inicio_contrato || ""}
                onChange={(e) => {
                  const inicio = e.target.value;
                  // Empresa (nivel === 0): não auto-preenche Vigência Final.
                  // Demais perfis: auto-preenche Vigência Final = data de início + 2 anos - 1 dia.
                  const isEmpresa = Number(user?.acesso_autorizacao) === 0;
                  let fimAuto = "";
                  if (!isEmpresa && inicio) {
                    const [y, m, d] = inicio.split("-").map(Number);
                    const dtInicio = new Date(y, (m || 1) - 1, d || 1);
                    if (!isNaN(dtInicio.getTime())) {
                      const dtFim = new Date(
                        dtInicio.getFullYear() + 2,
                        dtInicio.getMonth(),
                        dtInicio.getDate() - 1, // encerra 1 dia antes de completar 2 anos
                      );
                      const yy = dtFim.getFullYear();
                      const mm = String(dtFim.getMonth() + 1).padStart(2, "0");
                      const dd = String(dtFim.getDate()).padStart(2, "0");
                      fimAuto = `${yy}-${mm}-${dd}`;
                    }
                  }
                  setFormData((prev) => ({
                    ...prev,
                    data_inicio_contrato: inicio,
                    data_fim_contrato: fimAuto,
                  }));
                }}
              >
                <option value="">Selecione</option>
                {datasVigenciaFuturas.map((d) => (
                  <option key={d.id} value={d.valorObj.data}>
                    {new Date(d.valorObj.data + "T00:00:00").toLocaleDateString(
                      "pt-BR",
                    )}
                  </option>
                ))}
              </select>
            </div>

            {Number(user?.acesso_autorizacao) !== 0 && (
              <div className="div-data-fim-contrato">
                <label className="all-label" htmlFor="data_fim_contrato">
                  Vigência Final:
                </label>
                <input
                  type="date"
                  name="data_fim_contrato"
                  id="data_fim_contrato"
                  value={formData.data_fim_contrato || ""}
                  onChange={(e) => {
                    const novaData = e.target.value;
                    if (novaData && formData.data_nascimento) {
                      const novaParts = novaData.split("-");
                      const nascParts = formData.data_nascimento
                        .substring(0, 10)
                        .split("-");
                      if (novaParts.length === 3 && nascParts.length === 3) {
                        const dtNova = new Date(
                          parseInt(novaParts[0], 10),
                          parseInt(novaParts[1], 10) - 1,
                          parseInt(novaParts[2], 10),
                        );
                        const limiteIdade = new Date(
                          parseInt(nascParts[0], 10) + 24,
                          parseInt(nascParts[1], 10) - 1,
                          parseInt(nascParts[2], 10),
                        );
                        limiteIdade.setDate(limiteIdade.getDate() - 1); // 1 dia antes

                        if (dtNova > limiteIdade) {
                          toastError(
                            "A vigência deve terminar antes de o aprendiz completar 24 anos.",
                          );
                          setFormData({
                            ...formData,
                            data_fim_contrato: "",
                          });
                          return;
                        }
                      }
                    }
                    setFormData({
                      ...formData,
                      data_fim_contrato: novaData,
                    });
                  }}
                  readOnly={!permissao?.podeEditarTudo}
                  disabled={submittingRef.current}
                />
              </div>
            )}
          </div>

          <div className="div-tca-four-3">
            <div className="div-curso">
              <label className="all-label" htmlFor="curso">
                Curso:
              </label>
              <select
                name="curso"
                id="curso"
                disabled={!permissao.podeEditarEmpresa}
                value={formData.curso || ""}
                onChange={(e) => {
                  handleChange(e);
                  const limite = getLimiteHorasAprendizagem(
                    cursosFiltrados,
                    e.target.value,
                  );
                  if (limite) {
                    setFormData((prev) => ({
                      ...prev,
                      carga_horario_curso: `${limite} horas diárias`,
                    }));
                  } else {
                    setFormData((prev) => ({
                      ...prev,
                      carga_horario_curso: "",
                    }));
                  }
                }}
              >
                <option value="">Selecione</option>
                {cursosFiltrados.map((c) => {
                  const valorUnico = `${c.valorObj.curso} (${c.valorObj.modalidade})`;
                  return (
                    <option key={c.id} value={valorUnico}>
                      {valorUnico}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="div-carga-horaria-curso">
              <label className="all-label" htmlFor="carga_horario_curso">
                Carga horária diária:
              </label>
              <input
                type="text"
                name="carga_horario_curso"
                id="carga_horario_curso"
                className="carga-horario-curso tca-form-control"
                readOnly
                value={formData.carga_horario_curso || ""}
              />
            </div>
            <div className="div-tca-salario">
              <label className="all-label" htmlFor="salario">
                Salário:
              </label>
              <NumericFormat
                name="salario"
                className="tca-salario"
                id="salario"
                disabled={!permissao.podeEditarEmpresa}
                value={formData.salario || ""}
                onValueChange={(values) => {
                  const { floatValue } = values;
                  setFormData((prev) => ({
                    ...prev,
                    salario: floatValue ?? 0,
                  }));
                }}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                fixedDecimalScale={true}
                prefix="R$ "
              />
            </div>
          </div>

          <div className="tca-horarios-row">
            <div className="horario-teorica aprendizagem-teorica">
              <div className="horario-card moderno">
                <h4>
                  Horário da aprendizagem teórica
                  {municipioAtualTca ? (
                    <small> ({municipioAtualTca})</small>
                  ) : null}
                </h4>
                <select
                  name="horario_teorica"
                  id="horario_teorica"
                  className="tca-form-control"
                  disabled={
                    !permissao.podeEditarEmpresa ||
                    horariosTeoricosDisponiveis.length === 0
                  }
                  value={formData.horario_teorica || ""}
                  onChange={handleChange}
                >
                  <option value="" disabled>
                    Selecione
                  </option>
                  {horariosTeoricosDisponiveis.map((h) => (
                    <option key={h.id} value={h.id}>
                      {formatHorarioTeoricoLabel(h)}
                    </option>
                  ))}
                </select>
                {horariosTeoricosDisponiveis.length === 0 && (
                  <small
                    style={{
                      display: "block",
                      marginTop: "8px",
                      color: "#b45309",
                    }}
                  >
                    Nenhum horário cadastrado para o município da empresa.
                  </small>
                )}
              </div>
            </div>

            <div className="horario-pratica aprendizagem-pratica">
              <div
                className="div-horario-fixo-alternado"
                style={{ marginBottom: "15px" }}
              >
                <label className="all-label" htmlFor="horario_tipo">
                  Tipo do horário da aprendizagem prática (Fixo ou Alternado)
                </label>
                <select
                  name="horario_tipo"
                  className="tca-form-control"
                  id="horario_tipo"
                  value={horario.tipo}
                  onChange={(e) =>
                    setHorario((prev) => ({ ...prev, tipo: e.target.value }))
                  }
                >
                  <option value="fixo">Horário Fixo</option>
                  <option value="alternado">Horário Alternado</option>
                </select>
              </div>

              {horario.tipo === "fixo" && (
                <div className="horario-card moderno">
                  <h4>
                    Horário Fixo da aprendizagem prática{" "}
                    <small>(mesmo horário todos os dias selecionados)</small>
                  </h4>
                  <div className="horario-grid">
                    <div className="dias-semana">
                      <label>Dias da semana de atuação *</label>
                      {["Segunda", "Terça", "Quarta", "Quinta", "Sexta"].map(
                        (dia) => {
                          const key = dia
                            .toLowerCase()
                            .replace("ç", "c")
                            .replace("á", "a")
                            .replace("ã", "a");
                          // Empresa (nivel 0) não pode desmarcar os dias da semana.
                          // Mantém os dias fixos (segunda a sexta) sempre marcados.
                          const isChecked =
                            isEmpresaNivel0 ||
                            horario.diasSelecionados.includes(dia);
                          const isDisabled = isEmpresaNivel0;
                          return (
                            <label
                              key={dia}
                              className="checkbox-dia"
                              style={
                                isDisabled
                                  ? { opacity: 0.85, cursor: "not-allowed" }
                                  : undefined
                              }
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                disabled={isDisabled}
                                onChange={
                                  isDisabled
                                    ? undefined
                                    : (e) => {
                                        const novos = e.target.checked
                                          ? [...horario.diasSelecionados, dia]
                                          : horario.diasSelecionados.filter(
                                              (d) => d !== dia,
                                            );
                                        setHorario((prev) => ({
                                          ...prev,
                                          diasSelecionados: novos,
                                        }));
                                      }
                                }
                              />
                              {dia}
                            </label>
                          );
                        },
                      )}
                    </div>

                    <div className="horarios">
                      <div>
                        <label>Horário de entrada *</label>
                        <input
                          type="time"
                          value={horario.fixo.entrada}
                          onChange={(e) =>
                            setHorario((prev) => ({
                              ...prev,
                              fixo: { ...prev.fixo, entrada: e.target.value },
                            }))
                          }
                        />
                      </div>
                      <div>
                        <label>Horário de saída *</label>
                        <input
                          type="time"
                          value={horario.fixo.saida}
                          onChange={(e) =>
                            setHorario((prev) => ({
                              ...prev,
                              fixo: { ...prev.fixo, saida: e.target.value },
                            }))
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {horario.tipo === "alternado" && (
                <div className="horario-card moderno">
                  <h4>
                    Horário Alternado da aprendizagem prática{" "}
                    <small>(horários diferentes por dia)</small>
                  </h4>
                  <table className="horario-grade">
                    <thead>
                      <tr>
                        <th>Dia da semana</th>
                        <th>Entrada</th>
                        <th>Saída</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { key: "segunda", label: "Segunda" },
                        { key: "terca", label: "Terça" },
                        { key: "quarta", label: "Quarta" },
                        { key: "quinta", label: "Quinta" },
                        { key: "sexta", label: "Sexta" },
                      ].map((dia) => (
                        <tr key={dia.key}>
                          <td>{dia.label}</td>
                          <td>
                            <input
                              type="time"
                              value={horario.alternado[dia.key].entrada}
                              onChange={(e) =>
                                setHorario((prev) => ({
                                  ...prev,
                                  alternado: {
                                    ...prev.alternado,
                                    [dia.key]: {
                                      ...prev.alternado[dia.key],
                                      entrada: e.target.value,
                                    },
                                  },
                                }))
                              }
                            />
                          </td>
                          <td>
                            <input
                              type="time"
                              value={horario.alternado[dia.key].saida}
                              onChange={(e) =>
                                setHorario((prev) => ({
                                  ...prev,
                                  alternado: {
                                    ...prev.alternado,
                                    [dia.key]: {
                                      ...prev.alternado[dia.key],
                                      saida: e.target.value,
                                    },
                                  },
                                }))
                              }
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {horario.tipo &&
                (() => {
                  const { errors } = validarHorarios(horario);
                  if (errors.length === 0) return null;
                  return (
                    <div className="carga-horaria-info">
                      <ul className="horario-errors">
                        {errors.map((err, i) => (
                          <li key={i}>⚠️ {err}</li>
                        ))}
                      </ul>
                    </div>
                  );
                })()}

              {erroHorarioAprendizagem && (
                <div
                  className="aviso-horario"
                  role="alert"
                  aria-live="polite"
                  data-testid="aviso-horario-aprendizagem"
                >
                  <AlertCircle size={18} />
                  <span>{erroHorarioAprendizagem}</span>
                </div>
              )}
            </div>
          </div>

          <br />
          <h6
            className="tca-h6"
            style={{ textAlign: "center", width: "100%", color: "#fff" }}
          >
            Resposavel pelas Avaliações
          </h6>
          <br />

          <div className="div-tca-four-4">
            <div className="div-nome-tutor">
              <label className="all-label" htmlFor="nome_tutor">
                Nome do responsável para receber as avaliações práticas:
              </label>
              <input
                type="text"
                name="nome_tutor"
                className="nome-tutor"
                id="nome_tutor"
                disabled={!permissao.podeEditarEmpresa}
                maxLength={70}
                onChange={handleChange}
                value={formData.nome_tutor}
              />
            </div>

            <div className="div-cargo-tutor">
              <label className="all-label" htmlFor="cargo_tutor">
                Cargo do(a) tutor(a):
              </label>
              <input
                type="text"
                name="cargo_tutor"
                className="cargo-tutor"
                id="cargo_tutor"
                disabled={!permissao.podeEditarEmpresa}
                maxLength={70}
                onChange={handleChange}
                value={formData.cargo_tutor}
              />
            </div>

            <div className="div-email-tutor">
              <label className="all-label" htmlFor="email_tutor">
                E-mail do responsável para receber as avaliações práticas:
              </label>
              <input
                type="email"
                name="email_tutor"
                className="email-tutor"
                id="email_tutor"
                disabled={!permissao.podeEditarEmpresa}
                maxLength={100}
                value={formData.email_tutor || ""}
                onChange={handleChange}
                style={
                  emailErrors.email_tutor
                    ? {
                        borderColor: "#ff6b6b",
                        boxShadow: "0 0 0 2px rgba(255,107,107,0.25)",
                      }
                    : undefined
                }
              />
              {emailErrors.email_tutor && (
                <span
                  style={{ color: "#ff6b6b", fontSize: "0.8rem", marginTop: 4 }}
                >
                  E-mail inválido.
                </span>
              )}
            </div>
          </div>

          <br />
          <h6
            className="tca-h6"
            style={{ textAlign: "center", width: "100%", color: "#fff" }}
          >
            Endereço da Aprendizagem Teórica e Prática
          </h6>
          <br />
          <div
            className="div-tca-four-5"
            style={{ display: "flex", gap: "1rem", width: "100%" }}
          >
            <div className="div-endereco-aprendizagem" style={{ flex: 1 }}>
              <label className="all-label" htmlFor="endereco_tutor">
                Endereço completo da aprendizagem prática:
              </label>
              <input
                type="text"
                name="endereco_tutor"
                className="endereco-aprendizagem"
                id="endereco_tutor"
                disabled={
                  !permissao.podeEditarEmpresa ||
                  (isEmpresaNivel0 && !formData.local_execucao_diferente)
                }
                readOnly={isEmpresaNivel0 && !formData.local_execucao_diferente}
                maxLength={200}
                onChange={handleChange}
                value={formData.endereco_tutor}
              />
            </div>

            <div className="div-endereco-teoria" style={{ flex: 1 }}>
              <label className="all-label" htmlFor="endereco_atividade_teorica">
                Endereço da atividade teórica:
              </label>
              <input
                type="text"
                name="endereco_atividade_teorica"
                className="endereco-teorica bg-white-important"
                id="endereco_atividade_teorica"
                readOnly={Number(user?.acesso_autorizacao) !== 2}
                onChange={handleChange}
                value={formData.endereco_atividade_teorica || ""}
              />
            </div>
          </div>
          <div className="div-fundo-txa">
            <div className="div-txa-obs-ajuste">
              <label
                className="lbl-textarea-ativs-aprendiz"
                htmlFor="atividades_aprendiz"
              >
                Atividades do aprendiz na empresa:
              </label>
              <select
                className="tca-atividades-select"
                name="atividades_aprendiz"
                id="atividades_aprendiz"
                disabled={
                  permissao?.podeEditarTudo
                    ? false
                    : !formData.curso ||
                      (!permissao?.podeEditarEmpresa &&
                        !permissao?.podeEditarTudo)
                }
                value={formData.atividades_aprendiz || ""}
                onChange={isStudent ? undefined : handleChange}
                onMouseDown={isStudent ? (e) => e.preventDefault() : undefined}
                tabIndex={isStudent ? -1 : undefined}
                aria-readonly={isStudent}
                title={
                  isStudent ? "Campo somente leitura para estudante" : undefined
                }
              >
                <option value="">
                  {formData.curso
                    ? "Selecione uma atividade"
                    : "Selecione um curso primeiro"}
                </option>
                {atividadesFiltradas.map((a) => (
                  <option key={a.id} value={a.atividade}>
                    {a.atividade}
                  </option>
                ))}
              </select>
            </div>

            {permissao?.podeVerObservacoesCiee && (
              <div
                className="div-observacoes"
                style={{
                  background: "rgba(30, 41, 59, 0.55)",
                  borderRadius: 10,
                  padding: "16px 18px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                  border: "1px solid rgba(255, 255, 255, 0.06)",
                  boxSizing: "border-box",
                  marginTop: "1.25rem",
                  marginBottom: "1.5rem",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div
                    style={{
                      width: 4,
                      height: 16,
                      borderRadius: 2,
                      background: "#185fa5",
                    }}
                  />
                  <p
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      margin: 0,
                      color: "#fff",
                      letterSpacing: 0.2,
                    }}
                  >
                    CIEE/MG - Particularidades para emissão do contrato
                  </p>
                </div>

                <div className="div-txa-obs-ajuste-1">
                  <textarea
                    style={{
                      width: "100%",
                      boxSizing: "border-box",
                      borderRadius: 8,
                      border: "1px solid #cbd5e1",
                      padding: 10,
                      fontSize: 13,
                      fontFamily: "inherit",
                      resize: "none",
                      background: "#ffffff",
                      color: "#0f172a",
                      outline: "none",
                      transition: "border-color 0.18s ease",
                    }}
                    className="textarea-ativs-aprendiz"
                    name="textarea-ativs-aprendiz"
                    id="textarea-ativs-aprendiz"
                    placeholder="Este espaço deve ser utilizado para observações, particularidades ou informações adicionais para a emissão do contrato."
                    value={formData["textarea-ativs-aprendiz"]}
                    onChange={(e) => {
                      handleChange(e);
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="div-tca-seven">
          <div className="btn-wrapper">
            <button
              type="button"
              onClick={openReview}
              className="btn-cadastrar-tca"
            >
              Cadastrar
            </button>
            {permissao?.podeAprovar &&
              formData.status !== "APROVADO" &&
              formData.status !== "RECUSADO" && (
                <>
                  <button
                    type="button"
                    onClick={aprovar}
                    className="btn-aprovar"
                  >
                    Aprovar
                  </button>
                  <button
                    type="button"
                    onClick={recusar}
                    className="btn-recusar"
                  >
                    Recusar
                  </button>
                </>
              )}
          </div>
        </div>

        {modoEdicao && tcaHistorico.length > 0 && (
          <div className="tca-card tca-mb-4">
            <div className="tca-card-header">Histórico de Transições</div>
            <div className="tca-card-body">
              <ul className="tca-historico-list">
                {tcaHistorico.map((h, i) => {
                  const corAnterior = getStatusCor(
                    h.status_anterior || "início",
                  );
                  const corNovo = getStatusCor(h.status_novo);

                  return (
                    <li key={i} className="tca-historico-item">
                      <div className="tca-historico-status">
                        <span
                          className="tca-historico-badge"
                          style={{ background: corAnterior, color: "#fff" }}
                        >
                          {h.status_anterior || "início"}
                        </span>
                        <span className="tca-historico-seta">→</span>
                        <span
                          className="tca-historico-badge"
                          style={{ background: corNovo, color: "#fff" }}
                        >
                          {h.status_novo}
                        </span>
                      </div>

                      <small className="tca-historico-info">
                        Por: {h.usuario_nome || h.usuario_id} ({h.usuario_tipo})
                      </small>
                      <small className="tca-historico-info">
                        {new Date(h.created_at).toLocaleString("pt-BR")}
                      </small>

                      {h.observacao && (
                        <div className="tca-historico-observacao">
                          {h.observacao}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}
      </form>

      {showReview && (
        <div
          className="tca-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowReview(false);
          }}
          role="dialog"
          aria-modal="true"
        >
          <div className="tca-modal-content">
            <FormReview
              data={buildReviewData()}
              onEdit={() => setShowReview(false)}
              onSubmit={async (e) => {
                setShowReview(false);
                await handleSubmit(e);
              }}
            />
          </div>
        </div>
      )}

      {missingFieldsAlert.length > 0 && (
        <MissingFieldsAlert
          missingFields={missingFieldsAlert}
          onFieldClick={handleMissingFieldClick}
          onClose={() => setMissingFieldsAlert([])}
        />
      )}

      <ToastContainer toasts={toasts} onRemove={removeToast} />
      {confirmDialogComum}

      <SuccessSubmitModal
        isOpen={successModalData.isOpen}
        onClose={() => {
          setSuccessModalData({ ...successModalData, isOpen: false });
          window.location.reload();
        }}
        formId={successModalData.tceId}
        role={successModalData.role}
        formType="TCA"
      />
    </>
  );
};

export default TCA;
